/* ============================================================================
   BELORA × SKIMS — davranış katmanı
   ----------------------------------------------------------------------------
   Prestige'in `assets/theme.js` bundle'ı satıcı tarafından derlenir ve ASLA
   düzenlenmez. SKIMS'e özgü tüm yeni davranış bu dosyada, `belora-` ön ekli
   custom element'ler olarak yaşar.

   NEDEN CUSTOM ELEMENT, NEDEN SECTION-SCOPED <script> DEĞİL
   Prestige'in Section Rendering API katmanı (theme.js:4378, :5405) HTML'i
   `innerHTML` / `outerHTML` ile yeniden yerleştirir. `innerHTML` ile eklenen
   `<script>` etiketleri ÇALIŞMAZ. Bu yüzden bir section'ın içine yazılan
   davranış ilk boyamada çalışır ve ilk AJAX güncellemesinden sonra ölür.
   Custom element'lerde bu sorun yoktur: bağlandıklarında otomatik upgrade
   olurlar — `innerHTML` ile eklenen düğümler dâhil.

   KATI KURALLAR
   1. Bu dosya Prestige olaylarını DİNLEYEBİLİR ve GÖNDEREBİLİR
      (`cart:change`, `cart:refresh`, `variant:add`, `line-item:change`,
      `dialog:before-show`, `dialog:after-show`, `dialog:before-hide`,
      `dialog:after-hide`, `dialog:force-close`).
   2. Bir satıcı elementini ASLA monkey-patch etmez, yeniden tanımlamaz veya
      üzerine `customElements.define()` yapmaz.
   3. Her element adı `belora-` ile başlar → 98 satıcı elementiyle sıfır
      çakışma garantisi.
   4. Her animasyon `prefersMotion()` ile korunur. Motion güvenli değilse son
      durum SENKRON uygulanır — içerik asla görünmez kalmaz.

   Animasyon kütüphanesi mevcut importmap üzerinden paylaşılır
   (layout/theme.liquid:41-50 → "vendor" → vendor.min.js). İkinci bir kütüphane
   yüklenmez; easing'ler Prestige'in kendi zaman çizelgeleriyle ortaktır.

   Faz planı — bu dosyaya eklenecek element'ler:
     Faz 3  <belora-promo-carousel>  <belora-header-height>
     Faz 4  <belora-flyout>
     Faz 5  <belora-marquee>
     Faz 6  <belora-card-swatches>
     Faz 7  <belora-size-guide>      <belora-sticky-atc>
     Faz 10 <belora-reveal>
   ============================================================================ */

// eslint-disable-next-line no-unused-vars
import { animate, inView, stagger, timeline } from 'vendor';

/* --------------------------------------------------------------------------
 * Ortak yardımcılar
 * ------------------------------------------------------------------------ */

/**
 * Prestige'in media query haritasını tek doğruluk kaynağı olarak kullanır
 * (snippets/js-variables.liquid → window.themeVariables.mediaQueries).
 * Harita yoksa güvenli bir yedeğe düşer.
 */
function mediaQuery(name) {
  const map = window.themeVariables?.mediaQueries;
  if (map && map[name]) return map[name];

  const fallback = {
    'motion-safe': '(prefers-reduced-motion: no-preference)',
    'motion-reduce': '(prefers-reduced-motion: reduce)',
    'supports-hover': 'screen and (pointer: fine)',
  };
  return fallback[name] || null;
}

/**
 * Animasyon oynatılmalı mı? Her `belora-*` element'i animasyona başlamadan
 * önce bunu sorar. `false` dönerse element son durumu doğrudan uygular.
 */
export function prefersMotion() {
  const q = mediaQuery('motion-safe');
  return q ? window.matchMedia(q).matches : true;
}

/**
 * Verilen media query şu an eşleşiyor mu?
 */
export function matches(name) {
  const q = mediaQuery(name);
  return q ? window.matchMedia(q).matches : false;
}

/**
 * Çift kayıt korumalı `customElements.define`.
 * Section Rendering API bir section'ı yeniden yerleştirdiğinde modül tekrar
 * değerlendirilmez, ama tema editöründe bölüm yeniden yüklenmesi ve HMR benzeri
 * senaryolarda aynı adın iki kez tanımlanması "already been defined" hatası
 * fırlatır ve modülün geri kalanını durdurur. Bu sarmalayıcı onu engeller.
 *
 * Ayrıca `belora-` ön ekini zorunlu kılarak satıcı elementlerinin yanlışlıkla
 * ezilmesini imkânsız hâle getirir (kural 2 ve 3).
 */
export function define(name, ctor) {
  if (!name.startsWith('belora-')) {
    console.warn(`[belora] "${name}" reddedildi: element adı "belora-" ile başlamalı.`);
    return false;
  }
  if (customElements.get(name)) return false;

  customElements.define(name, ctor);
  return true;
}

/* --------------------------------------------------------------------------
 * Hata ayıklama / doğrulama yüzeyi
 * Faz 2 kabul testi bunu kullanır: konsolda `Belora.loaded === true`.
 * ------------------------------------------------------------------------ */
window.Belora = Object.freeze({
  loaded: true,
  version: 'SK2',
  prefersMotion,
  matches,
  define,
});

/* --------------------------------------------------------------------------
 * Element kayıtları
 * ------------------------------------------------------------------------ */

/**
 * <belora-header-height> — `--header-height` güvencesi.
 *
 * ⚠️ ÖNEMLİ BULGU: Prestige'in kendi `<height-observer variable="header">`
 * elementi bu değişkeni ZATEN yayınlıyor ve tam olarak aynı biçimi kullanıyor
 * (`${height.toFixed(2)}px`, assets/theme.js:530). skims-header.liquid içindeki
 * inline script bunu ikinci kez yapıyordu — aynı custom property'ye iki ayrı
 * ResizeObserver yazıyordu.
 *
 * Bu element bu yüzden ikinci bir yazıcı OLUŞTURMAZ. Yalnızca bir emniyet
 * kemeridir: bağlandığında Prestige'in observer'ının değişkeni gerçekten
 * yayınlayıp yayınlamadığını kontrol eder; yayınlıyorsa hiçbir şey yapmaz.
 * Yalnızca değişken yoksa (height-observer kaldırılmış, upgrade olmamış veya
 * ileride Prestige davranışı değişmişse) devreye girer.
 *
 * Öznitelikler:
 *   variable="header"  yayınlanacak değişkenin adı (--<variable>-height)
 */
class BeloraHeaderHeight extends HTMLElement {
  #ro = null;
  #onResize = null;
  #active = false;

  get #varName() {
    return `--${this.getAttribute('variable') || 'header'}-height`;
  }

  #publish = () => {
    const target = this.firstElementChild || this;
    const h = target.getBoundingClientRect().height;
    document.documentElement.style.setProperty(this.#varName, `${h.toFixed(2)}px`);
  };

  connectedCallback() {
    if (this.#active) return; // connectedCallback birden fazla kez çalışabilir
    this.#active = true;

    // Prestige zaten yayınlıyorsa devreye girme. Bir sonraki frame'de bakılır;
    // height-observer'ın upgrade olup ilk değeri yazması için zaman tanır.
    requestAnimationFrame(() => {
      if (!this.#active) return;
      const existing = getComputedStyle(document.documentElement)
        .getPropertyValue(this.#varName)
        .trim();
      if (existing) return; // Prestige height-observer iş başında — hiçbir şey yapma

      this.#publish();
      if (window.ResizeObserver) {
        this.#ro = new ResizeObserver(this.#publish);
        this.#ro.observe(this.firstElementChild || this);
      } else {
        // ResizeObserver yoksa yeniden boyutlandırmaya bağlan (rAF ile sınırlanır)
        let queued = false;
        this.#onResize = () => {
          if (queued) return;
          queued = true;
          requestAnimationFrame(() => { queued = false; this.#publish(); });
        };
        window.addEventListener('resize', this.#onResize, { passive: true });
      }
    });
  }

  disconnectedCallback() {
    this.#active = false;
    if (this.#ro) { this.#ro.disconnect(); this.#ro = null; }
    if (this.#onResize) { window.removeEventListener('resize', this.#onResize); this.#onResize = null; }
  }
}

/**
 * <belora-promo-carousel> — header promo şeridi.
 *
 * skims-header.liquid'in inline script'inin yerini alır. Inline script section
 * id'sine göre `document.getElementById` yapıyor, hiçbir dinleyiciyi veya
 * interval'i temizlemiyordu; Theme Editor bölümü yeniden yüklediğinde ya ölüyor
 * ya da çift interval bırakıyordu. Countdown interval'i hiç saklanmıyordu.
 *
 * Bu element:
 *  - section id'si veya global tekil selector varsaymaz; her şeyi kendi
 *    alt ağacından veya en yakın `.skims-header`'dan çözer, dolayısıyla aynı
 *    sayfada birden fazla örneğe dayanıklıdır,
 *  - disconnectedCallback'te tüm interval, dinleyici ve MQL aboneliklerini
 *    temizler,
 *  - connectedCallback iki kez çalışsa bile çift kurulum yapmaz.
 *
 * Öznitelikler:
 *   data-interval="5000"   autoplay periyodu (ms). Yoksa/0 ise autoplay kapalı.
 */
class BeloraPromoCarousel extends HTMLElement {
  #slides = [];
  #toggle = null;
  #interval = 0;
  #timer = null;
  #countdownTimer = null;
  #motionMQL = null;
  #idx = 0;

  // Duraklatma sebepleri ayrı tutulur: sekmeye geri dönüldüğünde kullanıcının
  // elle duraklattığı carousel yeniden başlamamalı.
  #userPaused = false;
  #hovering = false;

  #active = false;
  #listeners = [];

  #on(target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.#listeners.push([target, type, fn, opts]);
  }

  #motionOK() {
    return this.#motionMQL ? !this.#motionMQL.matches : true;
  }

  #shouldRun() {
    return this.#slides.length > 1
      && this.#interval > 0
      && this.#motionOK()
      && !this.#userPaused
      && !this.#hovering
      && !document.hidden;
  }

  /** Tek giriş noktası: koşullara göre interval'i kurar ya da kaldırır. */
  #sync = () => {
    if (this.#shouldRun()) {
      if (!this.#timer) this.#timer = setInterval(this.#next, this.#interval);
    } else if (this.#timer) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  };

  #show(i) {
    this.#idx = i;
    this.#slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
  }

  /* Süresi dolmuş sayaç slaytı `hidden` olur; rotasyon onu ATLAR, yoksa
     duyuru çubuğu bir tur boyunca boş görünürdü. Görünür slayt kalmadıysa
     bulunduğu yerde durur. */
  #next = () => {
    const total = this.#slides.length;
    if (!total) return;
    for (let step = 1; step <= total; step += 1) {
      const i = (this.#idx + step) % total;
      if (!this.#slides[i].hidden) { this.#show(i); return; }
    }
  };

  #syncToggleUI() {
    if (!this.#toggle) return;
    this.#toggle.setAttribute('aria-pressed', String(this.#userPaused));
    this.classList.toggle('skims-promo--paused', this.#userPaused);
    const label = this.#toggle.querySelector('[data-label-pause]');
    if (!label) return;
    const next = this.#userPaused
      ? this.#toggle.dataset.labelPlay
      : this.#toggle.dataset.labelPause;
    if (next) label.textContent = next;
  }

  /* ⚠️ SK7-FINAL-CONTENT.3 — kampanya sayacı
     Üç gerçek hata düzeltildi (canlıda ölçüldü, 2026-08-03):
       1. Biçim İngilizceydi (`0d 00h 00m 00s`) → Türkçe (`3 gün 04:12:07`).
       2. Süre dolduğunda slayt görünür kalıyor ve `0d 00h 00m 00s` yazıyordu
          → artık slayt tamamen DOM akışından çıkarılır (`hidden`), rotasyon
          onu atlar. Sahte şekilde yeniden başlamaz.
       3. `Date.parse('2026-08-31T23:59')` ziyaretçinin KENDİ saat diliminde
          çözülüyordu. Kampanya Europe/Istanbul'a göre biter; saat dilimi
          taşımayan değere `+03:00` eklenir. (TR yaz saati uygulamıyor,
          ofset yıl boyu sabit +03:00.) */
  #startCountdowns() {
    const nodes = [...this.querySelectorAll('.skims-countdown')];
    if (!nodes.length) return;
    const pad = (n) => String(n).padStart(2, '0');

    const parseDeadline = (raw) => {
      const v = (raw || '').trim().replace(' ', 'T');
      if (!v) return NaN;
      // Zaten saat dilimi taşıyorsa (Z veya ±hh:mm) olduğu gibi bırak.
      const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(v);
      return Date.parse(hasZone ? v : `${v}+03:00`);
    };

    const tick = () => {
      const now = Date.now();
      let allDone = true;
      nodes.forEach((el) => {
        const out = el.querySelector('[data-countdown-time]');
        const end = parseDeadline(el.getAttribute('data-countdown-end'));
        if (!out || Number.isNaN(end)) return;
        let diff = end - now;
        if (diff <= 0) {
          // Süresi dolan sayaç storefront'ta hiç görünmez.
          el.hidden = true;
          el.classList.remove('is-active');
          return;
        }
        allDone = false;
        el.hidden = false;
        const d = Math.floor(diff / 86400000); diff -= d * 86400000;
        const h = Math.floor(diff / 3600000); diff -= h * 3600000;
        const m = Math.floor(diff / 60000); diff -= m * 60000;
        const s = Math.floor(diff / 1000);
        out.textContent = d > 0
          ? `${d} gün ${pad(h)}:${pad(m)}:${pad(s)}`
          : `${pad(h)}:${pad(m)}:${pad(s)}`;
      });
      // Tüm sayaçlar bittiyse saniyelik tick'i durdur.
      if (allDone && this.#countdownTimer) {
        clearInterval(this.#countdownTimer);
        this.#countdownTimer = null;
      }
    };
    tick();
    this.#countdownTimer = setInterval(tick, 1000);
  }

  connectedCallback() {
    if (this.#active) return;
    this.#active = true;

    this.#slides = [...this.querySelectorAll('.skims-promo__msg')];
    this.#interval = parseInt(this.getAttribute('data-interval') || '0', 10) || 0;
    this.#idx = Math.max(0, this.#slides.findIndex((s) => s.classList.contains('is-active')));

    // Toggle butonu bu elementin DIŞINDA (topbar'ın sağ kümesinde). En yakın
    // header örneğinden çözülür — global arama veya section id varsayımı yok.
    const scope = this.closest('.skims-header') || this.parentElement;
    this.#toggle = scope ? scope.querySelector('[data-skims-promo-toggle]') : null;

    const q = mediaQuery('motion-reduce');
    if (q && window.matchMedia) {
      this.#motionMQL = window.matchMedia(q);
      // Kullanıcı sistem tercihini oturum sırasında değiştirirse canlı tepki ver.
      this.#on(this.#motionMQL, 'change', this.#sync);
    }

    if (this.#toggle) {
      this.#on(this.#toggle, 'click', () => {
        this.#userPaused = !this.#userPaused;
        this.#syncToggleUI();
        this.#sync();
      });
      this.#syncToggleUI();
    }

    this.#on(this, 'mouseenter', () => { this.#hovering = true; this.#sync(); });
    this.#on(this, 'mouseleave', () => { this.#hovering = false; this.#sync(); });

    // Sekme gizlenince durdur; geri dönünce YALNIZCA diğer koşullar uygunsa
    // devam et (#shouldRun zaten #userPaused'u kontrol ediyor).
    this.#on(document, 'visibilitychange', this.#sync);

    this.#startCountdowns();
    this.#sync();
  }

  disconnectedCallback() {
    this.#active = false;
    if (this.#timer) { clearInterval(this.#timer); this.#timer = null; }
    if (this.#countdownTimer) { clearInterval(this.#countdownTimer); this.#countdownTimer = null; }
    this.#listeners.forEach(([t, type, fn, opts]) => t.removeEventListener(type, fn, opts));
    this.#listeners = [];
    this.#motionMQL = null;
    this.#toggle = null;
    this.#slides = [];
  }
}

/**
 * <belora-hero-media> — hero medyası için hareket azaltma koordinatörü (SK5-B2).
 *
 * SORUN: CSS tek başına yetmiyor. `autoplay` özniteliği taşıyan bir <video>,
 * `display: none` ile gizlense bile arka planda oynamaya devam edebilir.
 * Ayrıca statik fallback yoksa videoyu gizlemek hero'yu boş bırakır.
 *
 * DAVRANIŞ
 *   reduce AÇIK  → `autoplay` düşürülür, video duraklatılır, currentTime 0'a
 *                  çekilir. Statik fallback varsa CSS videoyu gizler; yoksa
 *                  video görünür kalır ama İLK KAREDE DURUR (hero boş kalmaz).
 *   reduce KAPALI→ başlangıçta autoplay'liyse yeniden oynatılır.
 *
 * Tercih çalışma sırasında değişirse iki yönde de tepki verir. `play()`
 * promise'i sessizce yutulur — tarayıcı reddederse konsola hata düşmez.
 * Dinleyici `disconnectedCallback`'te sökülür; Theme Editor section reload'unda
 * çift kayıt olmaz.
 */
class BeloraHeroMedia extends HTMLElement {
  #video = null;
  #mql = null;
  #onPreferenceChange = null;
  #autoplayByDefault = false;

  connectedCallback() {
    this.#video = this.querySelector('video');
    if (!this.#video) return;

    this.#autoplayByDefault = this.#video.autoplay || this.#video.hasAttribute('autoplay');
    this.#mql = window.matchMedia(mediaQuery('motion-reduce') || '(prefers-reduced-motion: reduce)');
    this.#onPreferenceChange = () => this.#sync();
    this.#mql.addEventListener('change', this.#onPreferenceChange);
    this.#sync();
  }

  disconnectedCallback() {
    if (this.#mql && this.#onPreferenceChange) {
      this.#mql.removeEventListener('change', this.#onPreferenceChange);
    }
    this.#mql = null;
    this.#onPreferenceChange = null;
    this.#video = null;
  }

  #sync() {
    const video = this.#video;
    if (!video || !this.#mql) return;

    if (this.#mql.matches) {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.pause();
      // Yüklenmemiş videoda currentTime yazmak istisna fırlatabilir.
      try { if (video.currentTime > 0) video.currentTime = 0; } catch { /* yoksay */ }
      return;
    }

    if (!this.#autoplayByDefault) return;
    video.autoplay = true;
    const played = video.play();
    if (played && typeof played.catch === 'function') {
      played.catch(() => { /* tarayıcı reddi sessiz — konsola hata düşmez */ });
    }
  }
}

/**
 * BeloraWishlist — favori deposu (SK6-A).
 *
 * İZOLASYON KURALI: depolama ARKASINDA tek bir adaptör var. Bugün
 * `localStorage`, yarın hesap tabanlı bir uç nokta olabilir; değişmesi
 * gereken tek yer bu nesnenin `read`/`write` metotları. Buton bileşeni
 * depolamanın ne olduğunu bilmiyor, yalnız `has` / `toggle` / `subscribe`
 * sözleşmesini kullanıyor.
 *
 * Depo biçimi: ürün handle'larından oluşan düz bir dizi. `id` değil `handle`
 * saklanıyor — handle mağaza dışına taşınabilir ve URL'de zaten görünüyor.
 */
const BeloraWishlist = (() => {
  const KEY = 'belora:wishlist:v1';
  const listeners = new Set();

  function read() {
    try {
      const raw = window.localStorage.getItem(KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
    } catch {
      // Gizli sekme / dolu kota / bozuk JSON — favori özelliği sessizce
      // devre dışı kalır, sayfa çalışmaya devam eder.
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list));
    } catch { /* yoksay */ }
    listeners.forEach((fn) => { try { fn(list); } catch { /* yoksay */ } });
  }

  return {
    has(handle) { return read().indexOf(handle) !== -1; },
    toggle(handle) {
      const list = read();
      const i = list.indexOf(handle);
      if (i === -1) list.push(handle); else list.splice(i, 1);
      write(list);
      return i === -1;
    },
    all() { return read(); },
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
  };
})();

window.BeloraWishlist = BeloraWishlist;

/**
 * <belora-wishlist-button> — ürün kartındaki favori butonu (SK6-A).
 *
 * Ölçüm §7.1.2: 26×26 buton, 18×18 ikon, `aria-label="Add … to wishlist"`.
 * Dekoratif DEĞİL — gerçekten ekliyor/çıkarıyor ve sayfa yenilendiğinde
 * durumu koruyor (kullanıcı kararı 3).
 *
 * Erişilebilirlik: `aria-pressed` basılı durumu taşır, `aria-label` eylem
 * yönüne göre değişir. Aynı ürün sayfada birden fazla kez render edilirse
 * (ör. koleksiyon + ilgili ürünler) `subscribe` sayesinde hepsi eşzamanlı
 * güncellenir.
 */
class BeloraWishlistButton extends HTMLElement {
  #button = null;
  #unsubscribe = null;

  connectedCallback() {
    const handle = this.getAttribute('handle');
    if (!handle) return;

    // SK7-CART.1 — YENİDEN BAĞLANMADA DÜĞME ÇOĞALTMA DÜZELTMESİ
    // Sepet çekmecesi açılıp kapandığında çocuklarını koparıp yeniden
    // bağlıyor. `disconnectedCallback` ürettiği düğmeyi DOM'dan kaldırmadığı
    // için her yeniden bağlanma BİR DÜĞME DAHA ekliyordu (ölçüldü: çekmece
    // üçüncü açılışta `childElementCount = 3`). Artık mevcut düğme varsa
    // yeniden kullanılır.
    // ⚠️ Kilitli fazlar etkilenmez: PDP ve PLP'de element yeniden
    // BAĞLANMAZ, varyant değişiminde komple yeni örnek oluşur — o yolda
    // düğme zaten hep 1'di ve 1 kalır (ölçümle doğrulandı).
    this.#button = this.querySelector(':scope > .skims-card__wishlist-button');

    if (!this.#button) {
      this.#button = document.createElement('button');
      this.#button.type = 'button';
      this.#button.className = 'skims-card__wishlist-button';
      // SK6-A5: `stroke-width` 1.5 → 1. Canlı SKIMS favori ikonunda ölçülen
      // computed `stroke-width: 1px` (`class="size-4.5 stroke-1"`); uçlar
      // `stroke-linecap/linejoin: round`. Kutu ve ikon ölçüsü değişmedi
      // (26×26 / 18×18). Kalp yolu BELORA'nın kendi çizimi olarak kalıyor —
      // SKIMS'in path verisi projeye kopyalanmıyor.
      this.#button.innerHTML =
        '<svg class="skims-card__wishlist-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
        '<path d="M12 20.7 3.9 12.6a5 5 0 0 1 7.1-7.1l1 1 1-1a5 5 0 1 1 7.1 7.1z"/></svg>';
      this.appendChild(this.#button);
    }

    // Aynı fonksiyon referansıyla ikinci kez eklemek etkisizdir (DOM spec),
    // bu yüzden yeniden bağlanmada listener çoğalmaz.
    this.#button.addEventListener('click', this.#onClick);

    this.#sync();
    this.#unsubscribe = BeloraWishlist.subscribe(() => this.#sync());
  }

  disconnectedCallback() {
    if (this.#button) this.#button.removeEventListener('click', this.#onClick);
    if (this.#unsubscribe) this.#unsubscribe();
    this.#unsubscribe = null;
    this.#button = null;
  }

  #onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();      // kartın tam kaplayan linkine sızmasın
    BeloraWishlist.toggle(this.getAttribute('handle'));
  };

  #sync() {
    if (!this.#button) return;
    const active = BeloraWishlist.has(this.getAttribute('handle'));
    this.#button.setAttribute('aria-pressed', active ? 'true' : 'false');
    this.#button.setAttribute(
      'aria-label',
      (active ? this.getAttribute('label-remove') : this.getAttribute('label-add')) || ''
    );
    this.classList.toggle('is-active', active);
  }
}

define('belora-header-height', BeloraHeaderHeight);
define('belora-promo-carousel', BeloraPromoCarousel);
define('belora-hero-media', BeloraHeroMedia);
define('belora-wishlist-button', BeloraWishlistButton);

/**
 * <belora-collection-toolbar> — SK6-C2
 * ---------------------------------------------------------------------------
 * İKİ SORUMLULUĞU VAR:
 *
 * 1. İKİ DURUMLU STICKY TOOLBAR
 *    Canlı SKIMS ölçümü (1425): toolbar'ın doğal doküman ofseti 224.8px ve
 *    durum tam olarak `scrollY > 225`'te değişiyor — aşağı ve yukarı
 *    kaydırmada AYNI noktada (histerezis yok). Yani kural scroll YÖNÜ değil,
 *    "sayfa toolbar'ın doğal konumunu geçti mi".
 *    Eşik burada SABİT YAZILMIYOR: kapsayıcının doküman ofseti okunuyor.
 *    Kapsayıcı sticky DEĞİL (sticky olan içindeki `.skims-toolbar`), bu yüzden
 *    ofset her zaman doğal konumu verir.
 *
 * 2. FACET DÜĞMESİ → İLGİLİ AKORDEON
 *    Ölçüm: `COLOR` düğmesine basınca çekmece açılıyor ve YALNIZ Color grubu
 *    `aria-expanded="true"` oluyor; diğerleri kapalı. Eşleştirme görünen
 *    metne değil `data-facet-key`'e bağlı (sıralama 'sort', facet'ler
 *    `filter.param_name`) — locale değişse de bozulmaz.
 *
 * PERFORMANS
 *   `scroll` dinleyicisi `{ passive: true }` ve rAF ile throttle'lı; her
 *   karede tek okuma (`scrollY`), yazma yalnız durum GERÇEKTEN değişince.
 *   Dinleyici `connectedCallback`te eklenip `disconnectedCallback`te
 *   kaldırıldığı için Section Rendering API veya Theme Editor section
 *   reload'unda çoğalması yapısal olarak imkânsız.
 */
class BeloraCollectionToolbar extends HTMLElement {
  #bar = null;
  #naturalTop = 0;
  #collapsed = null;
  #ticking = false;
  #ro = null;

  connectedCallback() {
    this.#bar = this.querySelector('.skims-toolbar');
    if (!this.#bar) return;

    this.#measure();
    this.#apply(true);

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize, { passive: true });

    // Başlık/banner yüksekliği görsel yüklenince değişebilir; ofset yeniden ölçülür.
    if ('ResizeObserver' in window) {
      this.#ro = new ResizeObserver(() => this.#onResize());
      this.#ro.observe(document.documentElement);
    }

    this.addEventListener('click', this.#onClick);

    // Section yeniden render edildikten sonra durumu geri yükle (odak kurtarma).
    BeloraFacetState.restore(this);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    if (this.#ro) { this.#ro.disconnect(); this.#ro = null; }
    this.removeEventListener('click', this.#onClick);
    this.#bar = null;
  }

  /** Doğal doküman ofseti — sticky çocuk değil, KAPSAYICI ölçülür. */
  #measure() {
    this.#naturalTop = this.getBoundingClientRect().top + window.scrollY;
  }

  #onResize = () => { this.#measure(); this.#apply(); };

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => { this.#ticking = false; this.#apply(); });
  };

  #apply(force) {
    const collapsed = window.scrollY > this.#naturalTop;
    if (!force && collapsed === this.#collapsed) return;   // gereksiz yazma yok
    this.#collapsed = collapsed;
    this.#bar.toggleAttribute('data-collapsed', collapsed);
  }

  #onClick = (event) => {
    const trigger = event.target.closest('[data-facet-key]');
    if (!trigger || !this.contains(trigger)) return;
    BeloraFacetState.openGroup(trigger.getAttribute('data-facet-key'));
  };
}

/**
 * Çekmece durum yöneticisi — SK6-C2.
 *
 * ODAK KURTARMA SORUNU: Prestige filtre/sıralama değişiminde Section
 * Rendering API ile TÜM section'ı değiştiriyor; çekmeceyi açan düğme DOM'dan
 * siliniyor ve odak `<body>`'ye düşüyor. Canlı SKIMS bir SPA olduğu için
 * düğüm korunuyor ve odak yerinde kalıyor.
 *
 * ÇÖZÜM: eski element referansı SAKLANMAZ (o düğüm artık yok). Yalnız kararlı
 * `data-facet-key` dizesi saklanır; section yeniden render edildikten sonra
 * yeni DOM'da aynı anahtar YENİDEN SORGULANIR ve odak ona taşınır.
 */
const BeloraFacetState = (() => {
  const KEY = '__beloraFacetState';
  const get = () => window[KEY] || (window[KEY] = { key: null, drawerOpen: false });

  function drawer() { return document.getElementById('facets-drawer'); }

  /** Verilen anahtara karşılık gelen akordeonu açar, diğerlerini kapatır. */
  function expand(key) {
    const dr = drawer();
    if (!dr || !key) return null;
    let target = null;
    dr.querySelectorAll('accordion-disclosure[data-facet-key]').forEach((acc) => {
      const details = acc.querySelector('details');
      if (!details) return;
      const match = acc.getAttribute('data-facet-key') === key;
      // Ölçüm: SKIMS'te tıklanan grup AÇIK, diğerlerinin hepsi KAPALI.
      details.open = match;
      details.setAttribute('aria-expanded', match ? 'true' : 'false');
      if (match) target = acc;
    });
    return target;
  }

  function openGroup(key) {
    const st = get();
    st.key = key;
    st.drawerOpen = true;
    // Çekmeceyi Prestige'in kendi `aria-controls` mekanizması açıyor; akordeon
    // seçimi bir sonraki kareye bırakılıyor ki çekmece içeriği bağlanmış olsun.
    requestAnimationFrame(() => {
      const target = expand(key);
      if (!target) return;
      target.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      const summary = target.querySelector('summary');
      if (summary) summary.focus({ preventScroll: true });
    });
  }

  /**
   * Section yeniden render edildikten sonra çağrılır. Çekmece açık kalmalıysa
   * yeniden açar, aynı grubu açar; değilse odağı yeni toolbar'daki AYNI
   * anahtarlı düğmeye taşır.
   */
  function restore(toolbar) {
    const st = get();

    // BAYAT ÇEKMECE TEMİZLİĞİ.
    // `<facets-drawer>` açıldığında kendini `<body>`'ye taşıyor. Filtre
    // değişince Section Rendering API section'ı yeniden basıyor ve section'ın
    // içinde YENİ bir `<facets-drawer>` doğuyor; eski, taşınmış olan ise
    // `<body>`de öksüz kalıyor. Sonuç: aynı `id`'ye sahip İKİ çekmece ve
    // ekranda eski (bayat) içerik — çipi kaldırınca çip ekranda kalıyordu.
    // Ölçüldü: `document.querySelectorAll('facets-drawer').length === 2`.
    if (st.staleDrawer && st.staleDrawer.isConnected) {
      st.staleDrawer.remove();
    }
    st.staleDrawer = null;

    if (!st.key) return;
    const dr = drawer();
    const trigger = toolbar.querySelector(`[data-facet-key="${CSS.escape(st.key)}"]`);

    if (st.drawerOpen && dr) {
      requestAnimationFrame(() => {
        if (!dr.hasAttribute('open') && typeof dr.show === 'function') dr.show();
        expand(st.key);
      });
      return;
    }
    if (trigger) trigger.focus({ preventScroll: true });
    st.key = null;
  }

  /** Çekmece kapandığında (ESC / backdrop / kapat düğmesi) odağı geri getirir. */
  function onDrawerClosed() {
    const st = get();
    st.drawerOpen = false;
    if (!st.key) return;
    const trigger = document.querySelector(
      `belora-collection-toolbar [data-facet-key="${CSS.escape(st.key)}"]`
    );
    if (trigger) trigger.focus({ preventScroll: true });
    st.key = null;
  }

  return { openGroup, restore, onDrawerClosed, expand };
})();

/**
 * SK6-C2 — ÇEKMECE İÇİ `facet-link` KÖPRÜSÜ.
 *
 * KÖK NEDEN: `<facets-drawer>` açıldığında kendini `<body>`'ye TAŞIYOR
 * (ölçüldü: ebeveyn zinciri FACETS-DRAWER → BODY → HTML). Prestige'in
 * `FacetLink` elementi ise `extractSectionId(event.target)` ile
 * `closest('.shopify-section')` arıyor; çekmece section'ın dışına çıktığı için
 * bu `null` dönüyor, `element.id` patlıyor ve `facet:update` olayı HİÇ
 * gönderilmiyor. Sonuç: seçili filtre çipine basınca hiçbir şey olmuyordu.
 *
 * Bu, `active-facets` bloğunu SKIMS parity'si için çekmece başlığına taşımanın
 * doğrudan sonucu — Prestige onu bilerek section'ın içinde tutuyordu.
 *
 * ÇÖZÜM: Prestige monkey-patch'lenmiyor (kural 2). Tıklama YAKALAMA fazında
 * ele alınıp `FacetLink`'in kendi dinleyicisine ulaşmadan durduruluyor ve
 * Prestige'in KENDİ `facet:update` sözleşmesi (kural 1) elle gönderiliyor.
 * Section id, çekmecenin içindeki `<facets-form section-id="…">`den okunuyor —
 * DOM konumundan bağımsız, kararlı kaynak.
 */
document.addEventListener(
  'click',
  (event) => {
    const link = event.target.closest?.('.skims-facets-drawer__header facet-link > a');
    if (!link) return;

    const form = document.querySelector('#facets-drawer facets-form[section-id]');
    const sectionId = form && form.getAttribute('section-id');
    if (!sectionId) return; // köprü kurulamıyorsa Prestige'in yoluna dokunma

    event.preventDefault();
    event.stopPropagation();   // FacetLink'in patlayan dinleyicisine ulaşmasın

    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    document.dispatchEvent(
      new CustomEvent('facet:update', { bubbles: true, detail: { url } })
    );
  },
  true
);

// Her facet güncellemesinde (çip kaldırma, onay kutusu, sıralama) o an AÇIK
// olan çekmece "bayat" diye işaretlenir; section yeniden basıldıktan sonra
// `restore()` onu DOM'dan kaldırır. Böylece çift `id` ve eski içerik oluşmaz.
document.addEventListener(
  'facet:update',
  () => {
    const st = window.__beloraFacetState || (window.__beloraFacetState = { key: null, drawerOpen: false });
    const open = document.getElementById('facets-drawer');
    if (open && open.hasAttribute('open')) {
      st.staleDrawer = open;
      st.drawerOpen = true;
    }
  },
  true
);

// Çekmece kapanışını yakala. Prestige `<dialog>` tabanlı olduğu için `close`
// olayı güvenilir; `toggle` de yedek olarak dinleniyor.
document.addEventListener(
  'close',
  (event) => {
    if (event.target && event.target.id === 'facets-drawer') BeloraFacetState.onDrawerClosed();
  },
  true
);

define('belora-collection-toolbar', BeloraCollectionToolbar);

/**
 * <belora-plp-status> — SK6-E / SK6-E2, PLP hata durumu.
 * ---------------------------------------------------------------------------
 * ÖLÇÜM ÖZETİ (canlı SKIMS): filtre değişiminde HİÇBİR yükleme göstergesi yok
 * — grid `opacity: 1`'de kalıyor, iskelet yok, spinner yok, `aria-busy` yok
 * (MutationObserver ile 3.4 sn örneklendi). Bu yüzden BELORA'ya iskelet
 * EKLENMEDİ; bu element yalnız HATA durumunu ele alır.
 *
 * SK6-E2 — TIMEOUT TABANLI TAHMİN KALDIRILDI.
 * Önce "`theme:loading:start` geldi ama 10 sn içinde `end` gelmedi → hata"
 * varsayımı kullanılıyordu. Güvenilir değildi: yavaş ama BAŞARILI bir istek
 * hata sayılabiliyor, "tekrar dene" ilk istek hâlâ sürerken ikinci bir istek
 * başlatabiliyor ve iki yanıt ters sırada dönebiliyordu.
 * Artık Prestige'in kendi `catch` dalından yayılan GERÇEK olay dinleniyor:
 *   `belora:facet:error` → { sectionId, url, search, message }
 * (bkz. `assets/theme.js` içindeki SK6-E2 bloğu.)
 *
 * BAYAT YANIT KORUMASI: Prestige zaten her yeni `facet:update`te öncekini
 * `abortController.abort()` ile iptal ediyor (`theme.js:2289`), yani eski
 * yanıt yeni DOM'u ezemez. İptal `catch`e düşer ama `AbortError` olarak
 * ayıklanır ve hata sayılmaz — ek bir request-id katmanı GEREKMİYOR.
 */
class BeloraPlpStatus extends HTMLElement {
  #box = null;
  #retry = null;
  #lastUrl = '';
  #pending = false;

  connectedCallback() {
    this.#box = this.querySelector('.skims-error');
    if (!this.#box) return;
    this.#retry = this.querySelector('[data-plp-retry]');

    document.addEventListener('facet:update', this.#onFacetUpdate, true);
    document.addEventListener('theme:loading:start', this.#onStart);
    document.documentElement.addEventListener('theme:loading:start', this.#onStart);
    document.addEventListener('theme:loading:end', this.#onEnd);
    document.documentElement.addEventListener('theme:loading:end', this.#onEnd);
    document.addEventListener('belora:facet:error', this.#onError);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    document.removeEventListener('facet:update', this.#onFacetUpdate, true);
    document.removeEventListener('theme:loading:start', this.#onStart);
    document.documentElement.removeEventListener('theme:loading:start', this.#onStart);
    document.removeEventListener('theme:loading:end', this.#onEnd);
    document.documentElement.removeEventListener('theme:loading:end', this.#onEnd);
    document.removeEventListener('belora:facet:error', this.#onError);
    this.removeEventListener('click', this.#onClick);
    this.#box = null;
    this.#retry = null;
  }

  /** Son denenen URL — "tekrar dene" AYNI isteği yeniden kurar. */
  #onFacetUpdate = (event) => {
    const u = event?.detail?.url;
    if (u) this.#lastUrl = u.toString();
  };

  #onStart = () => {
    this.#pending = true;
    this.#hide();
    this.#setRetryEnabled(false);   // istek sürerken çift tıklama imkânsız
  };

  #onEnd = () => {
    this.#pending = false;
    this.#setRetryEnabled(true);
  };

  #onError = (event) => {
    this.#pending = false;
    this.#setRetryEnabled(true);
    if (event?.detail?.url) this.#lastUrl = event.detail.url;
    this.#show();
  };

  #setRetryEnabled(on) {
    if (this.#retry) this.#retry.disabled = !on;
  }

  #show() {
    if (!this.#box) return;
    // TEK region: kutu section'ın içinde tek örnek; yeniden gösterilirken
    // içerik değişmediği için ikinci bir kutu asla oluşmaz.
    this.#box.hidden = false;
    // Çekmece kapalıysa kaydırma kilidi serbest bırakılıyor — kullanıcı
    // kilitli bir sayfada mahsur kalmasın.
    const drawer = document.getElementById('facets-drawer');
    if (drawer && !drawer.hasAttribute('open')) {
      document.documentElement.style.removeProperty('overflow');
    }
    // Odak ZORLA taşınmıyor; kutu `role="alert"` ile duyuruluyor ve gerçek
    // bir <button> içerdiği için Tab ile erişilebilir.
  }

  #hide() { if (this.#box) this.#box.hidden = true; }

  #onClick = (event) => {
    const btn = event.target.closest('[data-plp-retry]');
    if (!btn || btn.disabled || this.#pending) return;   // çift istek koruması
    this.#hide();
    this.#setRetryEnabled(false);
    this.#pending = true;

    const url = new URL(this.#lastUrl || window.location.href, window.location.origin);
    if (!url.searchParams.get('section_id')) {
      const form = document.querySelector('facets-form[section-id]');
      if (form) url.searchParams.set('section_id', form.getAttribute('section-id'));
    }
    // Prestige'in KENDİ sözleşmesi. `facet:update` içindeki
    // `history.replaceState` yeni bir geçmiş girdisi OLUŞTURMAZ.
    document.dispatchEvent(new CustomEvent('facet:update', { bubbles: true, detail: { url } }));
  };
}

define('belora-plp-status', BeloraPlpStatus);



/* --------------------------------------------------------------------------
 * SK4-D3 — Dialog odak geri dönüşü (yarış durumuna karşı sağlamlaştırılmış)
 *
 * SORUN 1: Prestige'in `DialogElement`'i kapanışta odağı açan tetikleyiciye
 * geri VERMEZ; odak `<body>`'ye düşer (WCAG 2.4.3).
 * SORUN 2: `vendor.min.js` focus-trap'i kapanıştan 0–50ms SONRA odağı
 * "trap'ten önceki" elemana döndürüyor ve `dialog:after-hide` anında yapılan
 * bir `focus()` çağrısını eziyor.
 *
 * ÇÖZÜM: dialog başına artan bir kapanış token'ı (generation) + iki aşamalı
 * doğrulama. Erken deneme `requestAnimationFrame`'de, NİHAİ ve yetkili karar
 * trap teardown'ından sonra tek seferlik `FINAL_DELAY` gecikmesinde verilir.
 * Satıcı sınıflarına dokunulmaz; monkey-patch / prototype override yok.
 * ------------------------------------------------------------------------ */

const FINAL_DELAY = 80; // ölçülen trap teardown penceresi 0–50ms; 80 pay bırakır

const dialogTrigger = new WeakMap(); // dialog -> tetikleyici
const wiredDialogs = new Set();      // sayfa ömrü boyunca birkaç adet
const pendingRestore = new Map();    // dialog -> { gen, timer }
let closeGeneration = 0;

const isOpen = (el) => el.hasAttribute('open');

const isFocusable = (el) =>
  Boolean(el) && el.isConnected && !el.disabled &&
  el.getAttribute('aria-hidden') !== 'true' && el.getClientRects().length > 0;

const otherDialogOpen = (self) => {
  for (const d of wiredDialogs) if (d !== self && isOpen(d)) return true;
  return false;
};

/** Hedefi ŞU AN kapalı olan bir dialog tetikleyicisi mi? Trap teardown'ı odağı
 *  böyle bayat bir tetikleyicide bırakabiliyor; bu "başıboş" sayılır. */
const isStaleTrigger = (el) => {
  if (!el || !el.matches || !el.matches('[aria-controls]')) return false;
  const target = document.getElementById(el.getAttribute('aria-controls'));
  return Boolean(target) && !isOpen(target);
};

/** Odak sahipsiz mi? Kullanıcının bilinçli seçtiği görünür bir elemansa değil. */
const focusIsLoose = (dialog, el) =>
  !el || el === document.body ||
  dialog.contains(el) ||
  Boolean(dialog.shadowRoot && dialog.shadowRoot.contains(el)) ||
  isStaleTrigger(el);

function attemptRestore(dialog, gen, isFinal) {
  const entry = pendingRestore.get(dialog);
  if (!entry || entry.gen !== gen) return;  // daha yeni bir kapanış/açılış var
  if (isFinal) pendingRestore.delete(dialog);

  if (isOpen(dialog) || otherDialogOpen(dialog)) return;
  const trigger = dialogTrigger.get(dialog);
  if (!isFocusable(trigger) || document.activeElement === trigger) return;
  if (!focusIsLoose(dialog, document.activeElement)) return;

  trigger.focus({ preventScroll: true });
}

function scheduleRestore(dialog) {
  const previous = pendingRestore.get(dialog);
  if (previous) clearTimeout(previous.timer);

  const gen = ++closeGeneration;
  const timer = setTimeout(() => attemptRestore(dialog, gen, true), FINAL_DELAY);
  pendingRestore.set(dialog, { gen, timer });
  requestAnimationFrame(() => attemptRestore(dialog, gen, false));
}

function wireDialog(dialog) {
  if (wiredDialogs.has(dialog)) return;
  wiredDialogs.add(dialog);
  dialog.addEventListener('dialog:after-hide', () => scheduleRestore(dialog));
}

// CAPTURE fazı: dialog açılmadan önce tetikleyiciyi kaydeder ve bekleyen tüm
// odak işlemlerini geçersiz kılar (yeni açılış eskisini iptal eder).
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest('[aria-controls]');
  if (!trigger) return;

  const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
  if (!dialog) return;

  pendingRestore.forEach(({ timer }) => clearTimeout(timer));
  pendingRestore.clear();

  wireDialog(dialog);
  dialogTrigger.set(dialog, trigger);
}, true);

// Tanılama yüzeyi. `window.Belora` donmuş (Object.freeze) olduğundan ona
// özellik eklenemez; ayrı ve salt-okunur bir global kullanılır.
window.BeloraFocusReturn = Object.freeze({
  stats: () => ({ wired: wiredDialogs.size, pending: pendingRestore.size }),
});

/**
 * CSS `ease` anahtar sözcüğü = `cubic-bezier(0.25, 0.1, 0.25, 1)`.
 * Yaklaşık bir formül YAZILMAZ; eğri gerçek Bézier tanımından çözülür:
 * x(t) için Newton-Raphson ile t bulunur, sonra y(t) döndürülür.
 * SK7-B.1'de ölçülen SKIMS geçişi bu eğriyi kullanıyor.
 */
function cssEase(x) {
  if (x <= 0) return 0;
  if (x >= 1) return 1;

  const X1 = 0.25;
  const Y1 = 0.1;
  const X2 = 0.25;
  const Y2 = 1;

  const bezier = (t, a, b) => {
    const p = 1 - t;
    return 3 * p * p * t * a + 3 * p * t * t * b + t * t * t;
  };
  const slope = (t, a, b) => {
    const p = 1 - t;
    return 3 * p * p * a + 6 * p * t * (b - a) + 3 * t * t * (1 - b);
  };

  let t = x;
  for (let i = 0; i < 8; i++) {
    const error = bezier(t, X1, X2) - x;
    if (Math.abs(error) < 1e-6) break;
    const d = slope(t, X1, X2);
    if (Math.abs(d) < 1e-6) break;
    t -= error / d;
  }

  return bezier(Math.min(1, Math.max(0, t)), Y1, Y2);
}

/* ==========================================================================
 * SK7-B.1 — GALERİ HAREKET SAHİPLİĞİ
 * --------------------------------------------------------------------------
 * ÖLÇÜLEN SKIMS (Swiper): ok tıklamasında hem medya şeridi hem scrollbar
 * thumb'ı `transform` üzerinde `0.3s ease` ile ~300ms'de oturuyor; sürükleme
 * sırasında transition `0s` (parmağı birebir takip); `prefers-reduced-motion`
 * açıkken de animasyon çalışıyor.
 *
 * PRESTIGE: `ScrollCarousel.select()` → `scrollTo({ behavior: 'smooth' })`,
 * yani TARAYICI native yumuşak kaydırması — süresi mesafeye bağlı, eğrisi UA
 * tanımlı. Fark buradan geliyor.
 *
 * MOBİL KÖK NEDEN (390 viewport'ta uçtan uca izlendi, tahmin yok):
 *   1. Mobilde galeri `scroll-snap-type: x mandatory` + `scroll-snap-stop:
 *      always`. Chromium bu durumda ARA `scrollLeft` yazımlarını KABUL ETMEZ;
 *      her yazımı en yakın snap noktasına çeker. Ölçüm: 20 karelik bir
 *      animasyonun 16'sı reddedildi (0 → 0 → 0 … → 390.4). Aynı test snap
 *      kapalıyken 21/21 kabul edildi.
 *   2. Snap'i geçici olarak `element.style` üzerinden kapatmak yetmiyor:
 *      rAF ile yapılan her `scrollLeft` yazımı tarayıcı için AYRI ve BİTMİŞ
 *      bir kaydırma olduğundan `scrollend` HER KAREDE tetikleniyor (300ms'de
 *      19 kez ölçüldü). Prestige'in `ScrollCarousel` `scrollend` dinleyicisi
 *      (`theme.js`, `#onScrollEnd`) ise `this.style.removeProperty(
 *      'scroll-snap-type')` çalıştırıp inline `none`'ı siliyor → mandatory
 *      snap geri geliyor → (1) devreye girip animasyonu ani sıçramaya
 *      çeviriyor. Aynı dinleyici ara konumdan indeks de hesapladığı için
 *      animasyon boyunca 19 sahte `carousel:settle` üretiyordu.
 *   Masaüstünde sorun görünmüyordu, çünkü ≥1024'te snap zaten `none`.
 *
 * SEÇİLEN SAHİPLİK MODELİ: TEK NATIVE SCROLL (A).
 * Kaydırma konumunun tek kaynağı `scrollLeft` olarak kalır; ikinci bir
 * transform pozisyon sistemi kurulmaz (drift ve re-render hatası üretirdi).
 * Prestige indeks/hedef hesabının sahibi olmayı sürdürür; bu katman yalnız
 * HAREKETİN sahibidir ve programatik geçiş boyunca:
 *   · snap'i kapalı tutar,
 *   · tarayıcının kare başına ürettiği `scrollend`'i temaya ULAŞTIRMAZ
 *     (`scrollend` baloncuk yapmadığı için yakalama fazında durdurulur),
 * böylece Prestige, native smooth scroll'dan aldığı sözleşmenin AYNISINI
 * alır: geçiş boyunca `scroll`, sonunda TEK bir `scrollend`.
 *
 * Aynı oturum scrollbar thumb sürüklemesinde de açılır: (1) yüzünden thumb
 * mobilde parmağı takip edemiyor, snap noktalarına atlıyordu (ölçüldü).
 * Bırakışta snap geri verilir — SKIMS'in `snapOnRelease` davranışı.
 * ======================================================================== */

/* Ölçülen SKIMS `speed: 300`. */
const GALLERY_MOTION_DURATION = 300;

function installGalleryMotion(scroller) {
  const nativeScrollTo = scroller.scrollTo.bind(scroller);
  const listeners = new AbortController();
  const { signal } = listeners;

  let frame = 0;
  let dragging = false;
  let closing = false;
  let snapHeld = false;
  let snapBefore = '';

  const holdSnap = () => {
    if (snapHeld) return;
    snapHeld = true;
    snapBefore = scroller.style.scrollSnapType;
    scroller.style.scrollSnapType = 'none';
  };

  const releaseSnap = () => {
    // Thumb sürüklemesi snap'in sahibidir; başka hiçbir yol onu erken geri veremez.
    if (!snapHeld || dragging) return;
    snapHeld = false;
    scroller.style.scrollSnapType = snapBefore;
  };

  const stopAnimation = () => {
    if (!frame) return;
    cancelAnimationFrame(frame);
    frame = 0;
  };

  /**
   * Sahipliğinde tuttuğumuz geçişi tema sözleşmesine göre kapatır.
   *
   * Tarayıcının kendi `scrollend`'ine güvenilemiyor: eased eğrinin son
   * kareleri alt piksele düştüğü için tarayıcı geçişi BİZDEN ÖNCE bitmiş
   * sayıp son `scrollend`'ini erken gönderiyor (o sırada hâlâ yutuluyor),
   * gerçek son yazım ise hiç hareket üretmediği için yenisi doğmuyor.
   * Ölçüm: aynı senaryonun 3 tekrarının 2'sinde Prestige HİÇ settle etmedi
   * → `carousel:settle` (medya durumu, adaptive height) ve
   * `#hasPendingProgrammaticScroll` sıfırlaması hiç çalışmadı.
   *
   * Geçişin sahibi biz olduğumuz için bitişi de biz bildiririz: tam olarak
   * TEK bir `scrollend` gönderilir, hemen ardından bir kare boyunca
   * tarayıcının geç kalan kopyası yutulur ki tema iki kez settle etmesin.
   */
  const closeTransition = () => {
    scroller.dispatchEvent(new Event('scrollend'));
    closing = true;
    requestAnimationFrame(() => {
      closing = false;
    });
  };

  // Kullanıcı kendi kaydırmasına başlarsa hareketin sahipliği derhal ona
  // geçer: animasyon bırakılır ve snap hemen geri verilir, böylece gestür
  // SKIMS'teki gibi en yakın slayta oturur. `pointerdown` kasıtlı olarak
  // dinlenmez — masaüstünde görsele tıklamak kaydırma niyeti değildir.
  const yieldToUser = () => {
    stopAnimation();
    releaseSnap();
  };
  scroller.addEventListener('wheel', yieldToUser, { passive: true, signal });
  scroller.addEventListener('touchstart', yieldToUser, { passive: true, signal });

  scroller.ownerDocument.addEventListener(
    'scrollend',
    (event) => {
      if (event.target === scroller && (frame || dragging || closing)) event.stopPropagation();
    },
    { capture: true, signal }
  );

  scroller.scrollTo = (options) => {
    const animatable =
      options !== null &&
      typeof options === 'object' &&
      options.behavior === 'smooth' &&
      typeof options.left === 'number';

    if (!animatable) {
      yieldToUser();
      return nativeScrollTo(options);
    }

    const from = scroller.scrollLeft;
    const distance = options.left - from;

    // Alt piksel mesafede kendi animasyonumuz görsel bir şey katmaz; olayları
    // eksiksiz üretmesi için tarayıcıya bırakılır.
    if (Math.abs(distance) < 1) {
      stopAnimation();
      return nativeScrollTo(options);
    }

    // Hedef animasyon sürerken değişirse (hızlı art arda tıklama) bulunulan
    // noktadan yeni hedefe geçilir; snap arada geri verilmez, yoksa tarayıcı
    // araya bir kez snap yapıp sıçrama üretirdi.
    stopAnimation();
    holdSnap();

    const start = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - start) / GALLERY_MOTION_DURATION);
      scroller.scrollLeft = from + distance * cssEase(progress);
      if (progress < 1) {
        frame = requestAnimationFrame(step);
        return;
      }
      frame = 0;
      releaseSnap();
      closeTransition();
    };
    frame = requestAnimationFrame(step);
  };

  return {
    beginThumbDrag() {
      dragging = true;
      stopAnimation();
      holdSnap();
    },
    endThumbDrag() {
      if (!dragging) return;
      dragging = false;
      // Snap geri gelince tarayıcı en yakın slayta oturtur (snapOnRelease).
      releaseSnap();
      closeTransition();
    },
    teardown() {
      stopAnimation();
      dragging = false;
      closing = false;
      releaseSnap();
      listeners.abort();
      // Prototip yöntemine dönülür; yeniden kurulumda sarmalayıcı zinciri
      // büyümez (variant re-render bu yolu her seferinde tekrar geçer).
      delete scroller.scrollTo;
    },
  };
}

/* ==========================================================================
 * SK7-B — GALERİ SCROLLBAR'I
 * --------------------------------------------------------------------------
 * Neden JS: bu ortamda tarayıcı overlay scrollbar kullanıyor ve
 * `::-webkit-scrollbar` kuralları yok sayılıyor — `height: 8px !important`
 * ile bile `offsetHeight - clientHeight === 0` ölçüldü. SKIMS'in 8px'lik
 * çubuğu native yolla üretilemiyor.
 *
 * Canlı SKIMS ölçümü (§8.1.3):
 *   track  : galeri genişliği × 8, bottom 0, z-index 50, radius 0,
 *            rgba(97, 94, 92, 0.25)
 *   thumb  : 8px, radius 0, rgb(45, 42, 38)
 *   Swiper : `scrollbar.draggable: true`, `snapOnRelease: true`
 *
 * Geometri HARDCODE EDİLMEZ; gerçek orandan türetilir:
 *   thumb genişliği = clientWidth / scrollWidth
 *   thumb konumu    = scrollLeft / (scrollWidth - clientWidth)
 * ======================================================================== */
class BeloraGalleryScrollbar extends HTMLElement {
  #scroller = null;
  #thumb = null;
  #frame = 0;
  #resizeObserver = null;
  #dragPointerId = null;
  #motion = null;
  #onScroll = () => this.#schedule();
  #onPointerDown = (event) => this.#startDrag(event);
  #onPointerMove = (event) => this.#drag(event);
  #onPointerUp = (event) => this.#endDrag(event);

  connectedCallback() {
    // `product-rerender` bu ağacı yeniden kurduğunda connectedCallback tekrar
    // çalışır. Önce her zaman sökülür, böylece dinleyici çoğalmaz.
    this.#teardown();

    this.#thumb = this.querySelector('.belora-gallery-scrollbar__thumb');
    const id = this.getAttribute('for');
    this.#scroller = id ? document.getElementById(id) : null;

    if (!this.#scroller || !this.#thumb) return;

    this.#motion = installGalleryMotion(this.#scroller);
    this.#scroller.addEventListener('scroll', this.#onScroll, { passive: true });
    this.addEventListener('pointerdown', this.#onPointerDown);

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#schedule());
      this.#resizeObserver.observe(this.#scroller);
    }

    this.#schedule();
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #teardown() {
    if (this.#frame) {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
    }
    if (this.#scroller) this.#scroller.removeEventListener('scroll', this.#onScroll);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerUp);
    window.removeEventListener('pointercancel', this.#onPointerUp);
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    if (this.#motion) {
      this.#motion.teardown();
      this.#motion = null;
    }
    this.#dragPointerId = null;
  }

  #schedule() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#render();
    });
  }

  #render() {
    const scroller = this.#scroller;
    if (!scroller || !this.#thumb) return;

    const scrollWidth = scroller.scrollWidth;
    const clientWidth = scroller.clientWidth;
    const overflow = scrollWidth - clientWidth;

    // Taşma yoksa çubuk anlamsız — tek medyalı üründe gizlenir.
    this.toggleAttribute('hidden', overflow <= 1);
    if (overflow <= 1) return;

    const ratio = clientWidth / scrollWidth;
    const progress = Math.min(1, Math.max(0, scroller.scrollLeft / overflow));

    this.#thumb.style.inlineSize = `${ratio * 100}%`;
    this.#thumb.style.insetInlineStart = `${progress * (100 - ratio * 100)}%`;
  }

  /* --- Sürükleme: SKIMS'te `scrollbar.draggable: true` (ölçüldü) --------- */
  #startDrag(event) {
    if (!this.#scroller || this.#dragPointerId !== null) return;
    this.#dragPointerId = event.pointerId;
    // Pointer capture başarısız olursa sürükleme yine de çalışmalı:
    // `setPointerCapture` aktif olmayan bir pointerId için `NotFoundError`
    // fırlatıyor ve yakalanmazsa `#seek`'e hiç ulaşılamıyor.
    try {
      this.setPointerCapture(event.pointerId);
    } catch {
      /* capture olmadan da window dinleyicileri sürüklemeyi yürütür */
    }
    window.addEventListener('pointermove', this.#onPointerMove);
    window.addEventListener('pointerup', this.#onPointerUp);
    window.addEventListener('pointercancel', this.#onPointerUp);
    // Thumb parmağı birebir takip etmeli (SKIMS'te sürüklerken transition 0s).
    // Snap açıkken tarayıcı ara `scrollLeft` yazımlarını reddediyor, thumb
    // slayttan slayta atlıyordu — `#seek`'ten ÖNCE oturum açılır.
    this.#motion?.beginThumbDrag();
    this.#seek(event);
  }

  #drag(event) {
    if (event.pointerId !== this.#dragPointerId) return;
    event.preventDefault();
    this.#seek(event);
  }

  #endDrag(event) {
    if (event.pointerId !== this.#dragPointerId) return;
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerUp);
    window.removeEventListener('pointercancel', this.#onPointerUp);
    this.#dragPointerId = null;
    // Bırakışta snap geri verilir → tarayıcı en yakın slayta oturtur.
    // SKIMS ölçümü: `scrollbar.snapOnRelease: true`.
    this.#motion?.endThumbDrag();
  }

  #seek(event) {
    const scroller = this.#scroller;
    if (!scroller) return;

    const track = this.getBoundingClientRect();
    const thumbWidth = track.width * (scroller.clientWidth / scroller.scrollWidth);
    const travel = track.width - thumbWidth;
    if (travel <= 0) return;

    const offset = event.clientX - track.left - thumbWidth / 2;
    const progress = Math.min(1, Math.max(0, offset / travel));
    scroller.scrollLeft = progress * (scroller.scrollWidth - scroller.clientWidth);
  }
}

define('belora-gallery-scrollbar', BeloraGalleryScrollbar);

/* ==========================================================================
 * SK7-F — ÜRÜN DETAY SEKMELERİ
 * --------------------------------------------------------------------------
 * Canlı SKIMS ölçümü (390 / 768 / 1024 / 1280 / 1440 / 1920 / 1024×700):
 *   · masaüstü ve mobilde AYNI yatay sekme sistemi — accordion YOK
 *   · `<ul role="tablist">` yatay kaydırılabilir (`overflow-x: auto`,
 *     `scrollbar-width: none`); 390'da scrollWidth 430 > clientWidth 375
 *   · her sekme `<button role="tab" aria-selected aria-controls>`
 *   · paneller DOM'da KALIYOR, pasif olanlar `display: none`
 *     (`hidden` özniteliği, `inert` veya opacity KULLANILMIYOR)
 *   · geçiş ANINDA — sekme, alt çizgi ve panel için ölçülen tüm
 *     `transition-duration` değerleri **0s**; height/fade animasyonu yok
 *   · sekme değişiminde sayfa kaydırma konumu değişmiyor, odak sekmede kalıyor
 *   · roving tabindex var (yalnız seçili sekme `tabindex="0"`)
 *
 * Neden Prestige'in `<x-tabs>`'ı kullanılmadı: o bileşen panel geçişinde
 * `translateY + opacity` animasyonu ve parametresi olmayan 0.15s'lik bir
 * yükseklik tween'i çalıştırıyor. SKIMS'te hiçbiri yok; `animation-duration`
 * özniteliği yükseklik tween'ini kapatmıyor. Ölçülen davranışı üretmek için
 * bu küçük bileşen yazıldı.
 *
 * ⚠️ Bilinçli erişilebilirlik FARKI: canlı SKIMS ok tuşlarına yanıt vermiyor
 * (ArrowLeft/Right/Home/End sentetik `keydown` ile denendi, seçim değişmedi).
 * Burada W3C APG "automatic activation" sözleşmesi uygulanır — Prestige'in
 * kendi `x-tabs`'ı da aynı sözleşmeyi kullanıyor, tema içi tutarlılık korunur.
 * ======================================================================== */
class BeloraProductTabs extends HTMLElement {
  #tabs = [];
  #panels = [];
  #listeners = null;

  connectedCallback() {
    this.#teardown();

    this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.#panels = this.#tabs.map((tab) => this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`));

    if (this.#tabs.length === 0) return;

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });

    const initial = this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.select(initial === -1 ? 0 : initial, { focus: false, reveal: false });
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #teardown() {
    if (this.#listeners) {
      this.#listeners.abort();
      this.#listeners = null;
    }
    this.#tabs = [];
    this.#panels = [];
  }

  #onClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    const index = this.#tabs.indexOf(tab);
    if (index === -1) return;
    // Sekmeler `<button type="button">`; yine de form içinde kalırlarsa
    // gönderim tetiklenmesin.
    event.preventDefault();
    this.select(index, { focus: false });
  };

  #onKeyDown = (event) => {
    const current = this.#tabs.indexOf(document.activeElement);
    if (current === -1) return;

    const last = this.#tabs.length - 1;
    let next = null;

    switch (event.key) {
      case 'ArrowLeft':
        next = current === 0 ? last : current - 1;
        break;
      case 'ArrowRight':
        next = current === last ? 0 : current + 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next, { focus: true });
  };

  /**
   * `reveal`: dar ekranda sekme şeridi yatay kayıyor; seçilen sekme görünür
   * alanın dışındaysa şeride kaydırılır. Sayfanın kendi dikey kaydırma
   * konumuna DOKUNULMAZ — SKIMS'te de sekme değişiminde sayfa kaymıyor,
   * bu yüzden `scrollIntoView` yerine şeridin `scrollLeft`'i yazılır.
   */
  select(index, { focus = false, reveal = true } = {}) {
    if (!(index in this.#tabs)) return;

    this.#tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
      const panel = this.#panels[i];
      if (panel) panel.toggleAttribute('data-belora-tab-hidden', !selected);
    });

    if (focus) this.#tabs[index].focus();
    if (reveal) this.#revealTab(this.#tabs[index]);
  }

  #revealTab(tab) {
    const list = tab.closest('[role="tablist"]');
    if (!list || list.scrollWidth <= list.clientWidth) return;

    const listBox = list.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();

    if (tabBox.left < listBox.left) {
      list.scrollLeft -= listBox.left - tabBox.left;
    } else if (tabBox.right > listBox.right) {
      list.scrollLeft += tabBox.right - listBox.right;
    }
  }
}

define('belora-product-tabs', BeloraProductTabs);

/* ==========================================================================
 * SK7-F — MODEL ÖLÇÜSÜ AÇ/KAPAT
 * --------------------------------------------------------------------------
 * Ölçülen SKIMS: metin kutusu kapalıyken yükseklik 0, `transition: all 0.3s`.
 * Yükseklik `auto`'ya animasyon edilemediği için gerçek içerik yüksekliği
 * ölçülüp piksel olarak yazılır; geçiş bitince `auto`'ya bırakılır ki içerik
 * (satır kırılması, yazı tipi yüklenmesi) sonradan değişirse kutu uyum sağlasın.
 * ======================================================================== */
class BeloraModelSizing extends HTMLElement {
  #button = null;
  #panel = null;
  #listeners = null;

  connectedCallback() {
    this.#teardown();

    this.#button = this.querySelector('.belora-model-sizing__button');
    this.#panel = this.querySelector('.belora-model-sizing__panel');
    if (!this.#button || !this.#panel) return;

    this.#listeners = new AbortController();
    this.#button.addEventListener('click', this.#toggle, { signal: this.#listeners.signal });
    this.#panel.addEventListener('transitionend', this.#onTransitionEnd, { signal: this.#listeners.signal });
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #teardown() {
    if (this.#listeners) {
      this.#listeners.abort();
      this.#listeners = null;
    }
  }

  #toggle = () => {
    const expanded = this.#button.getAttribute('aria-expanded') === 'true';
    this.#button.setAttribute('aria-expanded', expanded ? 'false' : 'true');

    if (expanded) {
      // Kapanış: `auto`'dan animasyon başlamaz, önce ölçülen değere sabitlenir.
      this.#panel.style.blockSize = `${this.#panel.scrollHeight}px`;
      requestAnimationFrame(() => {
        this.#panel.style.blockSize = '0px';
      });
      return;
    }

    this.#panel.hidden = false;
    this.#panel.style.blockSize = '0px';
    requestAnimationFrame(() => {
      this.#panel.style.blockSize = `${this.#panel.scrollHeight}px`;
    });
  };

  #onTransitionEnd = (event) => {
    if (event.propertyName !== 'block-size' && event.propertyName !== 'height') return;

    if (this.#button.getAttribute('aria-expanded') === 'true') {
      this.#panel.style.blockSize = 'auto';
    } else {
      this.#panel.hidden = true;
      this.#panel.style.removeProperty('block-size');
    }
  };
}

define('belora-model-sizing', BeloraModelSizing);

/* ==========================================================================
 * SK7-H.2C — BEDEN REHBERİ MODALI ERİŞİLEBİLİRLİK KONTROLCÜSÜ
 * --------------------------------------------------------------------------
 * H.2B'de iki gerçek hata açık kalmıştı. İkisi de `theme.js` + `vendor.min.js`
 * kaynağı okunarak ve tarayıcıda tekrar üretilerek KÖK NEDENE indirildi:
 *
 * 1. ODAK PANELE GİRMİYOR
 *    `DialogElement.initialFocus` (theme.js:1865) şu sırayı uyguluyor:
 *      `initial-focus` niteliği → `tabindex` niteliği → `querySelector('input')`
 *      → aksi hâlde `false`.
 *    Beden rehberi panelinde hiçbiri yok → `false`. focus-trap'te
 *    `initialFocus === false` HİÇBİR ZAMAN `fallbackFocus`'a düşmez
 *    (vendor.min.js `m()`: `if (a === false) return false`), bu yüzden
 *    Prestige'in `fallbackFocus: this` ayarı asla devreye girmiyordu — H.2B'de
 *    denenen `tabindex="-1"`in sonucu değiştirmemesinin sebebi de bu.
 *    Üstelik dokunmatikte Prestige `initialFocus`u zaten `false` sabitliyor
 *    (`matchesMediaQuery('supports-hover') ? this.initialFocus : false`).
 *    → Odak girişi bu kontrolcüde, `dialog:after-show` üzerinde yapılır.
 *
 * 2. ESCAPE KAPATMIYOR
 *    focus-trap dinleyicilerini (Tab + Escape) yalnız `finishActivation`
 *    içinde bağlıyor; Prestige bunu `checkCanFocusTrap: () => animation.finished`
 *    ile GİRİŞ ANİMASYONUNUN SONUNA erteliyor (0.2s masaüstü / 0.3s alt sayfa).
 *    O pencerede basılan Escape sessizce yutuluyor. Ölçüldü:
 *      tıklama + 50ms → Escape → modal AÇIK   (hata)
 *      tıklama + 600ms → Escape → modal kapalı (çalışıyor)
 *    Kullanıcının "Escape çalışmıyor" gözlemi bu erken penceredir.
 *    → Kontrolcü `dialog:before-show` ile kendi Escape dinleyicisini bağlar,
 *      `dialog:after-hide` ile kaldırır. Pencere kapanınca focus-trap kendi
 *      dinleyicisini zaten kurmuş olur; `hide()` idempotent olduğu için
 *      (theme.js:1804 `if (!this.open) return`) çift kapanma OLUŞMAZ.
 *
 * Satıcı elementi monkey-patch EDİLMEZ, `x-modal` yeniden tanımlanmaz, yeni
 * modal kütüphanesi eklenmez, zamanlayıcı zinciri kurulmaz (kural 1–3).
 * Odağın tetikleyiciye DÖNMESİ zaten SK4-D3 modülünün işi; burada tekrar
 * edilmez.
 * ======================================================================== */
class BeloraSizeGuide extends HTMLElement {
  #modal = null;
  #listeners = null;   // element ömrü boyunca
  #openCycle = null;   // tek bir açılış ↔ kapanış döngüsü

  connectedCallback() {
    this.#teardown();

    // Modal açılınca Prestige onu `document.body`'ye TAŞIR (shouldAppendToBody)
    // ve kapanınca buraya geri koyar. Bu yüzden referans bir kez çözülür ve
    // DOM konumundan bağımsız olarak elementin kendisi dinlenir.
    this.#modal = this.querySelector('x-modal') || document.getElementById(this.dataset.modal || '');
    if (!this.#modal) return;

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.#modal.addEventListener('dialog:before-show', this.#onBeforeShow, { signal });
    this.#modal.addEventListener('dialog:after-show', this.#onAfterShow, { signal });
    this.#modal.addEventListener('dialog:after-hide', this.#onAfterHide, { signal });
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #teardown() {
    this.#listeners?.abort();
    this.#listeners = null;
    this.#openCycle?.abort();
    this.#openCycle = null;
  }

  /** Escape dinleyicisi YALNIZ modal açıkken yaşar → bayat dinleyici olamaz. */
  #onBeforeShow = () => {
    this.#openCycle?.abort(); // savunma: aynı anda iki döngü asla olmaz
    this.#openCycle = new AbortController();
    document.addEventListener('keydown', this.#onKeydown, { signal: this.#openCycle.signal });
  };

  #onAfterHide = () => {
    // Hızlı kapat/aç: yeni açılış eski `after-hide`den önce gelirse dinleyici
    // sökülmemeli. Modal yeniden açıksa döngü YENİ döngüdür, dokunulmaz.
    if (this.#modal?.hasAttribute('open')) return;
    this.#openCycle?.abort();
    this.#openCycle = null;
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    if (event.defaultPrevented) return;
    if (!this.#modal?.hasAttribute('open')) return;
    // Üstte başka bir dialog varsa (ör. sepet çekmecesi) Escape ONUN işidir.
    if (!this.#isTopmostDialog()) return;

    event.preventDefault();
    this.#modal.hide();
  };

  /** Açık dialog'lar açılış sırasıyla `body`'ye eklenir → sonuncusu en üsttedir. */
  #isTopmostDialog() {
    const open = document.querySelectorAll('[role="dialog"][open]');
    return open.length === 0 || open[open.length - 1] === this.#modal;
  }

  #onAfterShow = () => {
    if (!this.#modal?.hasAttribute('open')) return;
    if (this.#focusIsInside()) return;

    const target = this.#focusTarget();
    if (!target) return;

    // Tek ve senkron çağrı: `after-show` giriş animasyonundan SONRA tetiklenir,
    // focus-trap'in kendi (etkisiz) ilk odak denemesi bundan sonra çalışır ve
    // `initialFocus === false` olduğu için odağı GERİ ALMAZ. Zamanlayıcı yok.
    target.focus({ preventScroll: true });
  };

  /** Kapatma düğmesi shadow DOM'da olduğu için normal `contains` yetmez. */
  #focusIsInside() {
    let node = document.activeElement;
    while (node?.shadowRoot?.activeElement) node = node.shadowRoot.activeElement;
    if (!node) return false;

    return node === this.#modal
      || this.#modal.contains(node)
      || Boolean(this.#modal.shadowRoot?.contains(node));
  }

  /** Brief'teki öncelik: kapatma düğmesi → panel başlığı → kaydırılabilir gövde. */
  #focusTarget() {
    const closeButton = this.#modal.shadowRoot?.querySelector('[part~="close-button"]');
    if (closeButton) return closeButton;

    const heading = this.#modal.querySelector('[slot="header"]');
    if (heading) {
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      return heading;
    }

    const body = this.#modal.querySelector('.belora-size-guide__body');
    if (body) return body;

    // Son çare: panelin kendisi programatik odak alır.
    if (!this.#modal.hasAttribute('tabindex')) this.#modal.setAttribute('tabindex', '-1');
    return this.#modal;
  }
}

define('belora-size-guide', BeloraSizeGuide);

/* ==========================================================================
 * SK7-B — VIDEO KONTROLLERİ
 * --------------------------------------------------------------------------
 * Canlı SKIMS ölçümü: video `autoplay + muted + loop + playsinline`,
 * native `controls` KAPALI, yerine iki adet 24 × 24 düğme
 * (`bottom: 6px`, sol/sağ `30px`, `-translate-x-1/2 -translate-y-1/2`).
 * `prefers-reduced-motion: reduce` altında bile autoplay AÇIK ölçüldü —
 * bu yüzden BELORA'da da keyfî olarak kapatılmıyor.
 *
 * aria-label eylemi anlatır ve gerçek video state'i ile senkron güncellenir.
 * ======================================================================== */
class BeloraVideoControls extends HTMLElement {
  #video = null;
  #playButton = null;
  #muteButton = null;
  #onState = () => this.#sync();
  #onPlayClick = (event) => {
    // Navigasyon/swipe davranışını bozmamak için olay burada durur.
    event.preventDefault();
    event.stopPropagation();
    if (!this.#video) return;
    if (this.#video.paused) {
      const started = this.#video.play();
      if (started && started.catch) started.catch(() => {});
    } else {
      this.#video.pause();
    }
  };
  #onMuteClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!this.#video) return;
    this.#video.muted = !this.#video.muted;
    this.#sync();
  };

  connectedCallback() {
    this.#teardown();

    const host = this.closest('.product-gallery__media') || this.parentElement;
    this.#video = host ? host.querySelector('video') : null;
    this.#playButton = this.querySelector('[data-belora-video-play]');
    this.#muteButton = this.querySelector('[data-belora-video-mute]');

    if (!this.#video) {
      this.toggleAttribute('hidden', true);
      return;
    }

    this.toggleAttribute('hidden', false);

    // ⚠️ BU ELEMENT OYNATMAYI YÖNETMEZ.
    // Oynatmanın tek sahibi `<video>` etiketindeki native `autoplay`'dir
    // (`snippets/media.liquid`, `native_autoplay`). Prestige'in
    // `video-media` / `autoplay-media` yönetimi bilinçli olarak kapalıdır —
    // yoksa aynı videoyu iki sistem kontrol eder. Burada yalnız kullanıcı
    // eylemleri ve arayüz state'i işlenir.
    for (const type of ['play', 'pause', 'volumechange']) {
      this.#video.addEventListener(type, this.#onState);
    }
    if (this.#playButton) this.#playButton.addEventListener('click', this.#onPlayClick);
    if (this.#muteButton) this.#muteButton.addEventListener('click', this.#onMuteClick);

    this.#sync();
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #teardown() {
    if (this.#playButton) this.#playButton.removeEventListener('click', this.#onPlayClick);
    if (this.#muteButton) this.#muteButton.removeEventListener('click', this.#onMuteClick);
    if (this.#video) {
      for (const type of ['play', 'pause', 'volumechange']) {
        this.#video.removeEventListener(type, this.#onState);
      }
    }
    this.#video = null;
    this.#playButton = null;
    this.#muteButton = null;
  }

  #sync() {
    const video = this.#video;
    if (!video) return;

    if (this.#playButton) {
      const paused = video.paused;
      const label = paused ? this.#playButton.dataset.labelPlay : this.#playButton.dataset.labelPause;
      if (label) this.#playButton.setAttribute('aria-label', label);
      this.#playButton.dataset.state = paused ? 'paused' : 'playing';
    }

    if (this.#muteButton) {
      const muted = video.muted;
      const label = muted ? this.#muteButton.dataset.labelUnmute : this.#muteButton.dataset.labelMute;
      if (label) this.#muteButton.setAttribute('aria-label', label);
      this.#muteButton.dataset.state = muted ? 'muted' : 'unmuted';
    }
  }
}

define('belora-video-controls', BeloraVideoControls);


/**
 * <belora-cart-badge> — header sepet sayacı rozetinin BOŞ durumu.
 *
 * ⚠️ BU ELEMENT SEPETİ DİNLEMEZ. Sayının tek sahibi Prestige'in kendi
 * `<cart-count>` elementidir (theme.js, CartCount): `cart:change`,
 * `cart:refresh` ve `pageshow` olaylarını dinleyip `innerText = item_count`
 * yazar. İkinci bir sepet dinleyicisi yazmak, aynı olayları iki kez işleyen
 * çift sözleşme demek olurdu (kural 2: satıcı elementi monkey-patch edilmez).
 *
 * Burada yalnız TEK bir eksik kapatılır: `<cart-count>` sıfırı da yazar ve
 * "0" rozeti hem SKIMS'te hem Prestige'in `cart-dot` sözleşmesinde yok. CSS
 * metin içeriğine göre seçim yapamadığı için sayı bir MutationObserver ile
 * izlenir ve `data-empty` açılıp kapanır — sepet mantığı çoğaltılmadan.
 *
 * İlk değeri sunucu basar (`{% if cart.item_count == 0 %}data-empty{% endif %}`),
 * bu yüzden ilk boyamada JS beklenmez ve rozet zıplamaz.
 */
class BeloraCartBadge extends HTMLElement {
  #observer = null;

  connectedCallback() {
    const count = this.querySelector('cart-count');
    if (!count) return;

    this.#sync(count);

    this.#observer = new MutationObserver(() => this.#sync(count));
    this.#observer.observe(count, { childList: true, characterData: true, subtree: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #sync(count) {
    const value = parseInt((count.textContent || '').trim(), 10);
    this.toggleAttribute('data-empty', !Number.isFinite(value) || value <= 0);
  }
}

define('belora-cart-badge', BeloraCartBadge);

/**
 * <belora-sticky-atc> — mobil sabit Sepete Ekle'nin görünürlük sahibi.
 *
 * SÖZLEŞME (kullanıcı kararı, SK7-E.4 madde 3)
 *   Sayfa içindeki gerçek Sepete Ekle viewport'ta GÖRÜNÜRKEN sabit buton
 *   GİZLİ; görünmüyorken GÖRÜNÜR. Yukarı kaydırıp normal butona dönüldüğünde
 *   tekrar gizlenir. Simetriktir: ATC ister yukarıda ister aşağıda kalsın,
 *   ölçüt yalnızca "viewport'ta görünüyor mu".
 *
 * ⚠️ NEDEN PRESTIGE'İN KENDİ `product-sticky-bar`'I KULLANILMADI
 * Prestige'in `ProductStickyBar` elementi (theme.js) iki ek koşul dayatıyor:
 *   1. `!isIntersecting && boundingClientRect.bottom < 0` → yalnız ATC'nin
 *      ÜSTÜNDEN geçilince gösterir. Sayfa başında (galeri) ATC henüz AŞAĞIDA
 *      olduğu için sabit buton çıkmaz; kullanıcının istediği B durumu
 *      ("galeri alanında sticky görünür olmalı") karşılanmaz.
 *   2. `&& footerCondition` → footer görününce gizler; istenen sözleşmede
 *      ölçüt yalnız ATC.
 * Ayrıca gözlemi FORM üzerinden yapıyor, istenen ise gerçek in-flow ATC.
 * Satıcı elementi monkey-patch edilmediği için (kural 2) görünürlüğün sahibi
 * bu element olur; Prestige'in observer'ı çalışmaya devam eder ama çıktısı
 * olan `.is-visible` sınıfı CSS'te TÜKETİLMEZ (aynı mimari karar SK7-E'de de
 * alınmıştı).
 *
 * EŞİK: canlı SKIMS'te ölçülebilecek bir eşik YOK — bu oturumda sabit buton
 * hiç render edilmedi, önceki oturumda ise eşiksiz sürekli görünürdü (A/B).
 * Bu yüzden eşik uydurulmadı: threshold 0 kullanılır, yani ATC'nin BİR PİKSELİ
 * bile görünüyorsa sabit buton gizlenir — kullanıcının cümlesinin birebir
 * karşılığı ("normal ATC viewport'ta görünürken sticky GÖRÜNMEYECEK").
 *
 * Öznitelikler:
 *   observe="<form id>"  gözlenecek ürün formunun id'si
 */
class BeloraStickyAtc extends HTMLElement {
  #observer = null;
  #bar = null;
  #listeners = null;

  /* Tek suppression state — iki bağımsız girdi, tek karar noktası (#apply).
     `#atcIsIntersecting` güvenli tarafta başlar (true = sabit buton GİZLİ):
     IntersectionObserver ilk kaydını bir sonraki frame'de verir. */
  #atcIsIntersecting = true;

  /* Açık overlay'ler SAYAÇ değil KÜME olarak tutulur. Sayaç `++/--` yaklaşımı
     çift `toggle` olayında veya kapanışı kaçırılan bir dialog'da negatife ya da
     kalıcı pozitife düşebilir; küme idempotenttir: aynı elementin iki kez
     eklenmesi tek kayıt üretir, silinmesi her zaman güvenlidir. */
  #openOverlays = new Set();

  connectedCallback() {
    this.#teardown();

    this.#bar = this.querySelector('product-sticky-bar');
    if (!this.#bar) return;

    const form = document.forms[this.getAttribute('observe')];
    // Sabit butonun kendisi forma `form=` ile bağlı ama form'un İÇİNDE değil;
    // bu yüzden form içindeki `buy-buttons` daima in-flow ATC'dir.
    const target = form?.querySelector('buy-buttons') ?? form;
    if (!target) return;

    this.#listeners = new AbortController();

    /* SK7-E.6 — OVERLAY KOORDİNASYONU
       Prestige'in `DialogElement.attributeChangedCallback`'i (theme.js:1954)
       her açılış VE kapanışta `new CustomEvent('toggle', { bubbles: true })`
       gönderiyor. Bu, temanın KENDİ lifecycle olayı; polling yok, sürekli
       MutationObserver yok, satıcı elementi monkey-patch edilmiyor.
       `dialog:before-show` / `dialog:after-hide` tercih edilmedi çünkü onlar
       `bubbles: true` DEĞİL — document'a hiç ulaşmıyorlar; her dialog'a tek tek
       abone olmak gerekirdi ve sonradan DOM'a giren bir drawer kaçırılırdı.
       `toggle` niteliğin değiştiği anda, giriş animasyonundan ÖNCE gelir →
       sabit buton ANINDA gizlenir.
       Yakalama fazı KULLANILMIYOR: yerel `<details>` de `toggle` gönderir ama
       BUBBLE etmez, bu yüzden bubble fazında yalnız Prestige dialog'ları gelir.
       Ek olarak `role="dialog"` filtresi var. */
    document.addEventListener('toggle', this.#onDialogToggle, { signal: this.#listeners.signal });

    /* Section re-render bir overlay AÇIKKEN olabilir (ör. sepete ekleme ürün
       bilgisini yeniden basar, çekmece açık kalır). Yeni element bağlandığında
       açık dialog'ları BİR KEZ tarar — sürekli gözlem değil. */
    document.querySelectorAll('[role="dialog"][open]').forEach((d) => this.#openOverlays.add(d));

    this.#apply();

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#atcIsIntersecting = entry.isIntersecting;
        this.#apply();
      },
      { threshold: 0 }
    );
    this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #teardown() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#listeners?.abort();
    this.#listeners = null;
    this.#openOverlays.clear();
    this.#atcIsIntersecting = true;
    this.#bar = null;
  }

  #onDialogToggle = (event) => {
    const dialog = event.target;
    if (!(dialog instanceof Element) || dialog.getAttribute('role') !== 'dialog') return;

    if (dialog.hasAttribute('open')) {
      this.#openOverlays.add(dialog);
    } else {
      this.#openOverlays.delete(dialog);
    }
    this.#apply();
  };

  /**
   * Üstte açık gerçek bir overlay var mı?
   *
   * Aynı anda birkaç dialog açık olabilir (ör. beden rehberi üstüne sepet
   * çekmecesi): biri kapanınca diğeri hâlâ açıksa küme boşalmaz ve sabit buton
   * GERİ GELMEZ. Okuma sırasında bayat kayıtlar temizlenir — bir dialog DOM'dan
   * kaldırılarak (section unload) kapatılmışsa `toggle` gelmemiş olabilir.
   * Bu döngü yalnız state değiştiğinde çalışır, zamanlayıcı ile DEĞİL.
   */
  #overlayOpen() {
    for (const dialog of this.#openOverlays) {
      if (!dialog.isConnected || !dialog.hasAttribute('open')) this.#openOverlays.delete(dialog);
    }
    return this.#openOverlays.size > 0;
  }

  /**
   * Görünürlüğü uygular.
   *
   * ⚠️ SINIF ADI PRESTIGE İLE PAYLAŞILMAZ. Ölçüldü: Prestige'in kendi
   * `ProductStickyBar`'ı AYNI element üzerinde `.is-visible` sınıfını kendi
   * koşuluyla (`footerCondition && formCondition`) sürekli yazıyor. O sınıf
   * kullanılınca footer görünür olur olmaz Prestige sınıfı KALDIRIYOR ve sabit
   * buton sayfa sonunda kayboluyordu — iki yazıcı aynı bayrak üzerinde
   * yarışıyordu. Bu yüzden görünürlüğün bayrağı BELORA'ya ait
   * `.belora-sticky-visible`; Prestige'in `.is-visible` çıktısı CSS'te
   * tüketilmediği için ikisi artık birbirine dokunmaz.
   *
   * Gizliyken buton:
   *   · `visibility: hidden` ile erişilebilirlik ağacından ve sekme
   *     sırasından çıkar (CSS'te),
   *   · `inert` ile odak ve pointer olayları motor düzeyinde de kapanır,
   *   · `aria-hidden="true"` ile (SK7-E.6) ekran okuyucu ağacından açıkça
   *     çıkarılır — z-index'e GÜVENİLMEZ: bar sabit konumlu ve `z-index: 40`,
   *     çekmece 999; panel onu ÖRTER ama %40 saydam backdrop'un ALTINDA pembe
   *     şerit olarak görünmeye devam ederdi (ölçüldü) ve erişilebilirlik
   *     ağacında kalırdı.
   *
   * TEK KARAR NOKTASI: `stickyShouldShow = !atcIsIntersecting && !overlayOpen`.
   * Overlay kapanınca körlemesine açılmaz — aynı ifade yeniden değerlendirilir,
   * yani in-flow ATC'nin GÜNCEL intersection durumu tekrar belirleyici olur.
   */
  #apply() {
    if (!this.#bar) return;

    const visible = !this.#atcIsIntersecting && !this.#overlayOpen();

    this.#bar.classList.toggle('belora-sticky-visible', visible);
    this.#bar.toggleAttribute('inert', !visible);
    if (visible) {
      this.#bar.removeAttribute('aria-hidden');
    } else {
      this.#bar.setAttribute('aria-hidden', 'true');
    }
  }
}

define('belora-sticky-atc', BeloraStickyAtc);

/**
 * <belora-variant-strip> — beden şeridinin yatay kaydırma konumunu korur.
 *
 * SORUN
 * Varyant seçilince Prestige'in `<product-rerender>`'ı `variant-picker`
 * bloğunu komple değiştiriyor (`theme.js` blok listesi `variant-picker`
 * içerir). Yeni düğüm doğal olarak `scrollLeft = 0` ile doğuyor; kullanıcı
 * sağa kaydırıp en sağdaki bedeni seçtiğinde şerit başa dönüyor ve SEÇTİĞİ
 * beden görünür alanın dışında kalıyordu (ölçüldü: scrollLeft 76.8 → 0,
 * seçili pill x = 389.6, şeridin sağ kenarı 370).
 *
 * ÖLÇÜLEN SKIMS DAVRANIŞI (2026-07-28 · 390 · /products/fits-everybody-t-shirt-bra-bubble-gum)
 * Şerit ölçüleri BELORA ile birebir aynı (scrollWidth 427 / clientWidth 350,
 * `overflow-x: auto`, `scroll-behavior: auto`, snap yok). Davranış:
 *     sağdayken (76.8) en sağdaki beden  → 76.8  (değişmedi)
 *     sağdayken (76.8) en soldaki beden  → 76.8  (değişmedi)
 *     sağdayken (76.8) ortadaki beden    → 76.8  (değişmedi)
 *     soldayken (0)    en sağdaki beden  → 0     (değişmedi)
 * SKIMS şeridi HİÇ otomatik kaydırmıyor; React düğümü yok etmediği için konum
 * kendiliğinden korunuyor. Bu yüzden burada da `scrollIntoView`, `center` veya
 * `nearest` UYGULANMAZ — yalnız konum geri yüklenir. Kullanıcının dokunduğu
 * pill zaten görünürdü, konum korununca görünür kalmaya devam eder.
 *
 * MEKANİZMA
 * Konumlar modül düzeyinde bir Map'te tutulur; element yok edilip yeniden
 * kurulduğunda YENİ örnek `connectedCallback`'te kendi anahtarını okuyup
 * konumu geri yükler. Böylece olay sırasına (`product:rerender` dinleyicileri
 * arasındaki yarış) hiç bağımlılık yoktur.
 *
 * Öznitelikler:
 *   data-strip-key  aynı ürünün re-render'ları arasında sabit anahtar
 */
const beloraVariantStripScroll = new Map();

class BeloraVariantStrip extends HTMLElement {
  #onScroll = null;

  connectedCallback() {
    const key = this.dataset.stripKey;
    if (!key) return;

    // Geri yükleme: yalnız gerçekten kaydırılabilir bir şeritte ve kayıtlı
    // konum varsa. `scroll-behavior` bilinçli olarak değiştirilmez — ölçülen
    // SKIMS değeri `auto`, yani anlık; smooth olsaydı re-render'da görünür bir
    // "geri kayma" animasyonu oluşurdu.
    const saved = beloraVariantStripScroll.get(key);
    if (saved) {
      // Düğüm yeni bağlandı; genişlikler bir sonraki karede kesinleşir.
      requestAnimationFrame(() => {
        const max = this.scrollWidth - this.clientWidth;
        if (max > 0) this.scrollLeft = Math.min(saved, max);
      });
    }

    this.#onScroll = () => beloraVariantStripScroll.set(key, this.scrollLeft);
    this.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    if (this.#onScroll) this.removeEventListener('scroll', this.#onScroll);
    this.#onScroll = null;
  }
}

define('belora-variant-strip', BeloraVariantStrip);

/**
 * <belora-cart-recently-viewed> — SK7-CART.1
 * ---------------------------------------------------------------------------
 * SEPET ÇEKMECESİ "Son Görüntülenenler" rayının okuyucusu.
 *
 * ⚠️ KAPSAM: YALNIZ sepet çekmecesi. PDP'ye Recently Viewed EKLENMEZ
 * (SK7-H.6A.1 kararı). Bu element PDP'de hiç örneklenmez.
 *
 * ⚠️ İKİNCİ BİR GEÇMİŞ SİSTEMİ DEĞİLDİR (§4):
 *   · anahtar : `theme:recently-viewed-products` — Prestige'in MEVCUT anahtarı
 *   · yazma   : `sections/main-product.liquid` satır içi script — DOKUNULMADI
 *   · okuma   : bu element
 *   · çözümleme: Shopify Section Rendering API — TEK istek
 * Canlı SKIMS `__kla_viewed` (Klaviyo) kullanıyor; BELORA o üçüncü taraf
 * anahtarına BAĞLANMAZ. Davranış parity'si korunur, sağlayıcı Prestige'tir.
 *
 * ÖLÇÜLEN FİLTRE SÖZLEŞMESİ (§5, izole edilerek doğrulandı)
 *   · bulunulan ürün → GÖSTERİLİR (filtrelenmez)
 *   · sepetteki ürün → FİLTRELENİR
 *   · aynı ürün tek kart (dedupe)
 *   · sıra: geçmişteki gibi en yeni → eski
 *
 * YAŞAM DÖNGÜSÜ (§11)
 * Prestige `cart:change` / `cart:refresh` olaylarında çekmeceyi sunucudan
 * yeniden render edip `replaceChildren` yapar → bu element HER SEFERİNDE
 * yeniden oluşturulur. Bu yüzden sonuç MODÜL SEVİYESİNDE, çözümlenen ID
 * listesine göre önbelleğe alınır: aynı liste için ikinci bir ağ isteği
 * ATILMAZ. Çekmece açılıp kapanması element'i yok etmediği için her açılışta
 * istek de olmaz.
 *
 * GİZLİLİK (§6): yalnız ürün ID'si okunur. Müşteri adı/e-posta/telefon/
 * adres/ödeme/form verisi ne okunur ne yazılır. Storage erişilemezse
 * (private mode) sessizce boş durum uygulanır.
 */
const BELORA_CART_RV_KEY = 'theme:recently-viewed-products';

/** Çözümlenen ID listesi → markup sözü. Çekmece re-render'ları arasında yaşar. */
const beloraCartRvCache = new Map();

class BeloraCartRecentlyViewed extends HTMLElement {
  #loaded = false;

  connectedCallback() {
    if (this.#loaded) return;
    this.#loaded = true;
    this.#load();
  }

  /**
   * Geçmişi güvenli çözer. Bozuk JSON, dizi olmayan veri, sayı olmayan ID,
   * sepetteki ürünler ve duplicate'ler elenir; sıra KORUNUR.
   * Hiçbir durumda throw etmez.
   */
  #ids() {
    let raw = null;

    try {
      raw = localStorage.getItem(BELORA_CART_RV_KEY);
    } catch (e) {
      return []; // storage erişilemiyor (private mode / kapalı çerez)
    }

    if (!raw) return [];

    let stored = null;
    try {
      stored = JSON.parse(raw);
    } catch (e) {
      return []; // bozuk JSON — çekmece KIRILMAZ, alan sessizce gizli kalır
    }

    if (!Array.isArray(stored)) return [];

    // Sepetteki ürünler ölçülen davranışa göre raydan çıkarılır.
    const inCart = new Set(
      (this.dataset.cartIds || '')
        .split(',')
        .map((value) => parseInt(value, 10))
        .filter((value) => Number.isFinite(value))
    );

    const limitRaw = parseInt(this.dataset.limit, 10);
    const limit = Number.isFinite(limitRaw) && limitRaw > 0 ? limitRaw : 5;

    const seen = new Set();
    const ids = [];

    for (const entry of stored) {
      const id = typeof entry === 'number' ? entry : parseInt(entry, 10);
      if (!Number.isFinite(id)) continue; // bozuk kayıt sessizce elenir
      if (inCart.has(id)) continue; // ÖLÇÜLEN: sepetteki ürün gösterilmez
      if (seen.has(id)) continue; // duplicate
      seen.add(id);
      ids.push(id);
      if (ids.length >= limit) break;
    }

    return ids;
  }

  async #load() {
    const ids = this.#ids();
    if (ids.length === 0) return; // boş geçmiş → İSTEK YOK, alan gizli kalır

    const key = ids.join(',');
    let markup = null;

    try {
      if (!beloraCartRvCache.has(key)) {
        beloraCartRvCache.set(key, this.#fetchMarkup(ids));
      }
      markup = await beloraCartRvCache.get(key);
    } catch (e) {
      beloraCartRvCache.delete(key);
      return; // ağ/parse hatası çekmeceyi KIRMAZ
    }

    if (!markup) return;

    // ⚠️ `createContextualFragment` KULLANILMAZ: canlı belge bağlamında
    // custom element'leri yükseltir, sonra düğümler taşınınca
    // bağlan → kopar → bağlan yaşanır. `BeloraWishlistButton`
    // `disconnectedCallback` içinde ürettiği düğmeyi DOM'dan kaldırmadığı
    // için bu, kart başına İKİ favori düğmesi basılmasına yol açıyordu
    // (ölçüldü: `childElementCount = 2`). `DOMParser` inert bir belge
    // ürettiğinden element orada yükseltilmez; `importNode` ile TEK sefer
    // bağlanır. Satıcı/wishlist elementi DEĞİŞTİRİLMEDİ.
    let cards = null;
    try {
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      // ⚠️ Section Rendering API çıktıyı `<div class="shopify-section">` ile
      // SARAR. Gövdenin çocuklarını olduğu gibi almak kartları rayın torunu
      // yapıyor, böylece flex öğesi olmuyorlar ve ölçülen 2.5-kart genişlik
      // sözleşmesi uygulanmıyordu (ölçüldü: 218px yerine 128.2px). Bu yüzden
      // sarmalayıcı atlanıp DOĞRUDAN kartlar alınır.
      cards = parsed.querySelectorAll('.skims-cartrv__card');
    } catch (e) {
      return;
    }

    if (!cards || cards.length === 0) return; // gerçek kart yok

    this.replaceChildren(...Array.from(cards, (card) => document.importNode(card, true)));

    // `hidden` YALNIZ burada — yani gerçekten kart basıldıktan sonra kalkar.
    const wrapper = this.closest('[data-skims-cartrv]');
    if (wrapper) wrapper.removeAttribute('hidden');
  }

  async #fetchMarkup(ids) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const query = ids.map((id) => `id:${id}`).join(' OR ');
    const url = `${root}search?type=product&q=${encodeURIComponent(query)}&section_id=belora-cart-recent-list`;

    const response = await fetch(url, { priority: 'low' });
    if (!response.ok) throw new Error('cart recently viewed: ' + response.status);
    return await response.text();
  }
}

define('belora-cart-recently-viewed', BeloraCartRecentlyViewed);

/* --------------------------------------------------------------------------
 * SK7-K.2A — ALT BİLGİ AÇILIR MENÜ (`aria-expanded` senkronu)
 * --------------------------------------------------------------------------
 * Ölçülen canlı SKIMS mobil footer'ında menü satırları `aria-expanded` taşıyan
 * düğmelerdir ve İKİSİ AYNI ANDA AÇIK KALABİLİR (exclusive değil).
 *
 * BELORA native `<details>/<summary>` kullanıyor: klavye (Enter/Space),
 * bağımsız açılma ve exclusive olmama davranışını tarayıcı ZATEN veriyor —
 * yeni accordion kütüphanesi yazılmadı. Eksik olan tek şey `aria-expanded`
 * özniteliğinin `open` durumuyla senkron kalmasıydı; bu element yalnız onu
 * yapar. Statik `aria-expanded` YAZILAMAZDI çünkü bayatlar.
 *
 * ≥1024'te CSS `<summary>`'yi `pointer-events: none` ile düz başlığa çevirir
 * ve `<details>` her zaman açık görünür; `aria-expanded` yine gerçek `open`
 * durumunu yansıtır.
 * -------------------------------------------------------------------------- */
class BeloraFooterAccordion extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    if (!this.details || !this.summary) return;

    this.desktop = window.matchMedia('(min-width: 1024px)');
    this.#sync();
    this.#applyMode();
    this.details.addEventListener('toggle', this.#sync);
    this.desktop.addEventListener('change', this.#applyMode);
  }

  disconnectedCallback() {
    if (this.details) this.details.removeEventListener('toggle', this.#sync);
    if (this.desktop) this.desktop.removeEventListener('change', this.#applyMode);
  }

  #sync = () => {
    this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
  };

  /**
   * >=1024'te acilir-kapanir davranis YOK: icerik her zaman gorunur ve
   * `<summary>` duz basliga doner.
   *
   * ⚠️ SK7-K.2B'de OLCULDU: bunu YALNIZ CSS ile yapmak ARTIK MUMKUN DEGIL.
   * Kapali `<details>` icerigini tarayici `::details-content` slotu uzerinde
   * `content-visibility: hidden` ile gizler; `.skims-footer__menu` uzerine
   * yazilan `display: flex` bu slotu EZEMEZ. Sonuc olculdu: 1440'ta menu
   * kolonlari 20px (yalniz baslik), linkler HIC boyanmiyor, `focus()` calismiyor
   * ve Tab sirasi footer'i atlayip belge basina donuyordu. Yani masaustu
   * footer'i "3 kolon" gorunup LINKSIZ kaliyordu.
   *
   * Dogru cozum, bolumun kendi sozlesmesini gercekten uygulamak: masaustunde
   * `<details>` GERCEKTEN acilir. Boylece icerik hem boyanir hem odaklanilabilir
   * olur ve `aria-expanded` dogru sekilde `true` olur.
   *
   * `<summary>` masaustunde sekme sirasindan CIKARILIR (`tabindex="-1"`):
   * olculen canli SKIMS'te de masaustunde accordion dugmesi sekme sirasinda
   * degildir. <1024'te oznitelik kaldirilir ve native klavye davranisi doner.
   *
   * Kirilim asilirken mobil taraf KAPALI duruma dondurulur — sartname
   * "<1024'te ikisi de baslangicta kapali" diyor; masaustunde acilan durum
   * mobile sizmamali.
   */
  #applyMode = () => {
    if (this.desktop.matches) {
      this.summary.setAttribute('tabindex', '-1');
      this.details.open = true;
    } else {
      this.summary.removeAttribute('tabindex');
      this.details.open = false;
    }
  };
}

define('belora-footer-accordion', BeloraFooterAccordion);

/* --------------------------------------------------------------------------
 * SK7-K.3A — SEPET BAŞLIĞI / KAPATMA DÜĞMESİ ERİŞİLEBİLİR ADI
 * --------------------------------------------------------------------------
 * Bu element GÖRSEL hiçbir şey yapmaz — başlığın metni ve tipografisi Liquid
 * ve CSS'ten gelir. TEK işi, sepet çekmecesinin GÖLGE KÖKÜNDEKİ kapatma
 * düğmesine sepete özgü bir erişilebilir ad vermektir.
 *
 * Neden JS: `snippets/shadow-dom-templates.liquid` içindeki
 * `drawer-default-template` TÜM çekmeceler tarafından paylaşılır (mobil menü,
 * arama, filtreler). Oradaki `aria-label`'ı değiştirmek bu fazın kapsamı
 * DIŞINDAKİ yüzeyleri de değiştirirdi. Gölge kök yazar CSS'i ile
 * biçimlendirilebilir ama öznitelik yazılamaz; bu yüzden tek düğüme özgü,
 * kapsamı dar bir yazma yapılır.
 *
 * ⚠️ `<cart-drawer>`ın IŞIK DOM'u her sepet mutasyonunda `innerHTML` ile
 * yeniden kurulur (theme.js:4378) — bu element de her seferinde yeniden
 * bağlanır ve etiketi tekrar uygular. GÖLGE kök ise yeniden kurulmaz.
 *
 * ⚠️ `requestAnimationFrame` KULLANILMAZ: gizli sekmede hiç çalışmaz ve
 * etiket kalıcı olarak uygulanmamış kalırdı (ölçüldü). `setTimeout` gizli
 * sekmede de ilerler.
 *
 * JS düşerse etiket Prestige'in "Kapat" değerinde kalır — bozulma DEĞİL.
 * -------------------------------------------------------------------------- */
class BeloraCartTitle extends HTMLElement {
  connectedCallback() {
    if (!this.#apply()) {
      // Gölge kök henüz kurulmamış olabilir (üst eleman yükseltme sırası).
      setTimeout(() => this.#apply(), 0);
    }
  }

  #apply = () => {
    const label = this.dataset.closeLabel;
    if (!label) return true; // yapacak iş yok

    const host = this.closest('cart-drawer');
    const button = host && host.shadowRoot && host.shadowRoot.querySelector('[part~="close-button"]');
    if (!button) return false;

    button.setAttribute('aria-label', label);
    return true;
  };
}

define('belora-cart-title', BeloraCartTitle);

/* --------------------------------------------------------------------------
 * SK7-K.3D.1B — SEPET ÇEKMECESİ ADET NİYET KOORDİNATÖRÜ
 * --------------------------------------------------------------------------
 * SORUN (ölçüldü): Prestige `LineItemQuantity` hedef miktarı
 * `<a href="...&quantity=N">` bağlantısından TIKLAMA ANINDA okur. Bu href
 * render anında yazılır ve ancak section yeniden render edilince (~1s)
 * tazelenir. Bu yüzden 1'den üç hızlı artı ÜÇ KEZ `quantity=2` gönderiyordu;
 * istekler idempotent olduğu için sonuç +1'de kalıyor, iki tıklama KAYBOLUYORDU.
 *
 * ⚠️ MUTATION SAHİPLİĞİ DEĞİŞMEZ. Bu dosya `/cart/change.js` çağrısını
 * ASLA kendisi yapmaz; her istek yine Prestige'in kendi handler'ından çıkar.
 * Custom element yeniden tanımlanmaz, script sırası değişmez, vendor olay
 * zinciri taklit edilmez.
 *
 * KULLANILAN VENDOR SİNYALLERİ (hepsi doğrulandı)
 *   `line-item:change`       → BAŞARI, `detail.quantity` sunucu doğrulamalı
 *   `cart-drawer:refreshed`  → yeni DOM hazır (theme.js:4440)
 *   `cart:error`             → HATA (SK7-K.3D.1A), `detail.message`
 * Arbitrary timeout ile başarı/hata TAHMİN EDİLMEZ.
 *
 * AKIŞ
 *   line boştaysa → tıklama vendor'a NORMAL ulaşır (tam 1 istek)
 *   inFlight iken → capture'da yutulur, YALNIZ targetQuantity güncellenir
 *   `line-item:change` → confirmed öğrenilir
 *   `cart-drawer:refreshed` → target farklıysa YENİ DOM'da aynı line bulunur,
 *     href final hedefe yazılır ve TEK takip tıklaması tetiklenir
 *   sonuç: hızlı seri = EN FAZLA 2 istek, dizi [2, 4]
 * -------------------------------------------------------------------------- */
const BeloraCartIntent = {
  states: new Map(),
  allowSynthetic: false,

  state(key) {
    if (!this.states.has(key)) {
      this.states.set(key, {
        confirmedQuantity: null,
        targetQuantity: null,
        inFlight: false,
        removeRequested: false,
        lastAction: null,
        generation: 0
      });
    }
    return this.states.get(key);
  },

  findHost(key) {
    const hosts = document.querySelectorAll('cart-drawer line-item-quantity');
    for (const host of hosts) {
      const input = host.querySelector('[data-line-key]');
      if (input && input.dataset.lineKey === key) return host;
    }
    return null;
  },

  renderedQuantity(host) {
    const input = host && host.querySelector('[data-line-key]');
    const n = input ? parseInt(input.defaultValue, 10) : NaN;
    return Number.isNaN(n) ? null : n;
  },

  setBusy(key, busy) {
    const host = this.findHost(key);
    const line = host && host.closest('line-item');
    if (line) line.toggleAttribute('aria-busy', busy);
  },

  lockCheckout(locked) {
    const button = document.querySelector('cart-drawer [name="checkout"]');
    if (!button) return;
    button.toggleAttribute('disabled', locked);
    if (locked) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
  },

  /**
   * SK18 — BAYAT `inFlight` TEMİZLİĞİ
   * Kanıt: kullanıcı "Ödemeye Geç" pasifken sayfayı yeniledi ve buton anında
   * aktif oldu. Yani Shopify sepeti doluydu; bozuk olan yalnız İSTEMCİ
   * belleğiydi.
   *
   * Kök mekanizma: `inFlight` YALNIZ `line-item:change` ile temizleniyordu.
   * O olayı taşıyan `line-item` elemanı kaldırma sonrası DOM'dan kalkıyor;
   * olay hiç gelmezse state bellekte `inFlight: true` kalıyor ve
   * `chainActive()` sonsuza dek `true` dönüyordu → checkout kalıcı pasif.
   *
   * `cart-drawer:refreshed` anında çekmece DOM'u SUNUCUDAN yeniden basılmıştır,
   * yani o an DOM otoritedir: karşılığı olmayan bir satırın mutasyonu BİTMİŞTİR.
   * Bu yüzden host'u bulunmayan state, `inFlight` olsun olmasın, düşürülür.
   * Sabit timeout KULLANILMAZ — karar gerçek DOM ve gerçek sepetten gelir.
   */
  pruneStaleStates() {
    for (const key of [...this.states.keys()]) {
      if (!this.findHost(key)) this.states.delete(key);
    }
  },

  /** Bekleyen gerçek işlem sayısı. DOM'da karşılığı olmayan satır SAYILMAZ. */
  pendingCount() {
    let n = 0;
    for (const [key, s] of this.states) {
      if (!this.findHost(key)) continue;
      if (s.inFlight) { n += 1; continue; }
      if (s.targetQuantity !== null && s.targetQuantity !== s.confirmedQuantity) n += 1;
    }
    return n;
  },

  chainActive() {
    return this.pendingCount() > 0;
  },

  /** Bekleyen iş kalmadığında takılı `aria-busy` izlerini siler. */
  clearStrayBusy() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    for (const el of drawer.querySelectorAll('[aria-busy]')) el.removeAttribute('aria-busy');
  },

  /** Butonu aktifleştirir ve takılı kalmış meşguliyet izlerini siler. */
  unlockCheckout() {
    this.lockCheckout(false);
    this.clearStrayBusy();
  },

  /**
   * SK18 — CHECKOUT DURUM UZLAŞTIRMASI
   * Buton durumu YALNIZ iki gerçek kaynaktan belirlenir:
   *   1) bekleyen gerçek sepet işlemi sayısı
   *   2) güncel sepetin `item_count` değeri
   * `/cart.js` yalnızca SONUCU DEĞİŞTİREBİLECEĞİ durumda çağrılır (bekleyen iş
   * yok ama buton hâlâ pasif) — her uzlaştırmada gereksiz istek atılmaz.
   */
  async reconcileCheckout() {
    this.pruneStaleStates();

    if (this.pendingCount() > 0) {
      this.lockCheckout(true);
      return;
    }

    const button = document.querySelector('cart-drawer [name="checkout"]');
    if (!button) return;
    // Bekleyen iş yoksa hiçbir satır "meşgul" işaretli kalmamalı. Hata yolunda
    // `cart:error` olayı satıra kadar çözülemezse `setBusy` anahtarla
    // temizlenemiyor ve satırda takılı `aria-busy` kalıyordu (ölçüldü: 422
    // enjekte edilen tur). Buton durumundan BAĞIMSIZ olarak temizlenir.
    this.clearStrayBusy();

    const looksLocked = button.hasAttribute('disabled') || button.getAttribute('aria-disabled') === 'true';
    if (!looksLocked) return; // zaten aktif; doğrulamaya gerek yok

    let itemCount = null;
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const cart = await (await fetch(`${root}cart.js`, { cache: 'no-store' })).json();
      itemCount = cart.item_count;
    } catch (e) {
      return; // doğrulanamadı: mevcut durumu BOZMA
    }

    // Uzlaştırma sırasında yeni bir işlem başlamış olabilir.
    if (this.pendingCount() > 0) return;
    if (itemCount > 0) this.unlockCheckout();
    else this.lockCheckout(true);
  },

  onClick(event) {
    const link = event.target.closest && event.target.closest('a[href*="/cart/change"]');
    if (!link || !link.closest('cart-drawer')) return;

    if (this.allowSynthetic) { this.allowSynthetic = false; return; }

    const url = new URL(link.href, location.origin);
    const key = url.searchParams.get('id');
    const hrefQty = parseInt(url.searchParams.get('quantity'), 10);
    if (!key) return;

    const host = link.closest('line-item-quantity');
    const rendered = this.renderedQuantity(host);
    // Render sonrası odağı geri verebilmek için son işlemi hatırla.
    this.lastFocusKey = key;
    const st = this.state(key);
    // ⚠️ Kaldırma ELEMENTTEN anlaşılır, sayıdan DEĞİL. Miktar 1 iken azaltma
    // düğmesinin href'i de meşru olarak `quantity=0` taşır; bunu kaldırma
    // saymak sepeti boşaltıyordu (ölçüldü: `+ + -` senaryosu 2 yerine 0
    // veriyordu). Artı/eksi düğmeleri `.quantity-selector__button`, kaldırma
    // bağlantısı ise `.quantity-selector` dışındaki ayrı `a.link`.
    const isRemove = !link.classList.contains('quantity-selector__button');

    if (st.confirmedQuantity === null && rendered !== null) st.confirmedQuantity = rendered;
    const base = st.targetQuantity === null ? st.confirmedQuantity : st.targetQuantity;

    if (isRemove) {
      st.removeRequested = true;
      st.targetQuantity = 0;
      st.lastAction = 'remove';
      this.lastActionKind = 'remove';
    } else if (!st.removeRequested) {
      const delta = (rendered !== null && !Number.isNaN(hrefQty)) ? hrefQty - rendered : 0;
      st.lastAction = delta < 0 ? 'decrease' : 'increase';
      this.lastActionKind = st.lastAction;
      const from = base === null ? rendered : base;
      st.targetQuantity = Math.max(1, from + delta);
    }

    if (st.inFlight) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const input = host && host.querySelector('[data-line-key]');
      if (input && !st.removeRequested) input.value = String(st.targetQuantity);
      return;
    }

    st.inFlight = true;
    st.generation += 1;
    this.setBusy(key, true);
    this.lockCheckout(true);
  },

  /**
   * SK7-K.3D.1B.1 — RENDER SONRASI ODAK GERİ DÖNÜŞÜ
   * Ölçüldü: Prestige mutation sonrası `replaceChildren` ile çekmeceyi yeniden
   * kuruyor; odaklanmış `<a>` yok olunca odak `<body>`ye, yani BELGE BAŞINA
   * düşüyor ve çekmecenin DIŞINA çıkıyordu.
   * ⚠️ Odak YALNIZ gerçekten kaybolduysa (activeElement `<body>`) geri verilir;
   * kullanıcı başka bir yere odaklandıysa odak ÇALINMAZ.
   */
  restoreFocus() {
    if (document.activeElement !== document.body) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !drawer.hasAttribute('open')) return;

    let target = null;
    const host = this.lastFocusKey ? this.findHost(this.lastFocusKey) : null;

    if (host) {
      const buttons = host.querySelectorAll('.quantity-selector__button');
      if (this.lastActionKind === 'decrease') {
        // SK7-K.3G — azaltma zinciri miktar 1'de bitince o yuva artık EKSİ
        // değil ÇÖP kontrolüdür (`quantity-selector__remove`, ayrı sınıf).
        // Odak YUVAYA döner, yani kullanıcının bastığı yere; `buttons[0]`
        // kullanılsa odak sessizce ARTI düğmesine, yani satırın diğer ucuna
        // atlıyordu (ölçüldü). `querySelector` virgüllü listede DOM sırasına
        // göre eşleştiği için 1. yuva her iki durumda da doğru bulunur.
        // ⚠️ Odak bilinçli olarak yıkıcı bir kontrolde durur — ölçülen SKIMS
        // de aynı yuvayı çöpe dönüştürüp odağı orada bırakıyor.
        target = host.querySelector('.quantity-selector__remove, .quantity-selector__button');
      } else if (this.lastActionKind === 'increase') target = buttons[buttons.length - 1];
      else target = host.querySelector('a.link') || buttons[buttons.length - 1];
    }

    // Satır kalktıysa (kaldırma) mantıklı bir çekmece kontrolüne dön.
    if (!target) target = drawer.querySelector('.belora-cart-empty__cta');
    if (!target && drawer.shadowRoot) target = drawer.shadowRoot.querySelector('[part~="close-button"]');
    if (target && typeof target.focus === 'function') target.focus();
  },

  onLineChange(event) {
    const line = event.target.closest && event.target.closest('line-item');
    const input = line && line.querySelector('[data-line-key]');
    const key = input ? input.dataset.lineKey : null;
    if (!key || !this.states.has(key)) return;
    const st = this.states.get(key);
    if (event.detail && typeof event.detail.quantity === 'number') {
      st.confirmedQuantity = event.detail.quantity;
    }
    st.inFlight = false;
    if (st.confirmedQuantity === 0) this.states.delete(key);
  },

  onRefreshed() {
    // SK18 — SUNUCU DOM'U TEK GERÇEK KAYNAKTIR
    // `cart-drawer:refreshed` anında çekmece SUNUCUDAN yeniden basılmıştır:
    // önceki mutasyon turu BİTMİŞTİR. Bu yüzden `inFlight` burada koşulsuz
    // düşürülür ve `confirmedQuantity` taze DOM'dan yeniden okunur.
    //
    // ⚠️ NEDEN `line-item:change`'e GÜVENİLMİYOR: o olay silinen satırla
    // birlikte kaybolabiliyor (ölçüldü). Dahası line item key aynı varyant
    // için SABİT olduğundan, silinip yeniden eklenen bir beden eski
    // `inFlight: true` state'ini DİRİLTİYOR ve checkout kalıcı pasif kalıyordu
    // (ölçüldü: sil → tekrar ekle). Olay yine kullanılır ama artık ZORUNLU
    // değildir; sunucu DOM'u her turda state'i mutlak biçimde uzlaştırır.
    for (const [key, st] of this.states) {
      st.inFlight = false;
      const freshHost = this.findHost(key);
      if (!freshHost) { this.states.delete(key); continue; }
      const rendered = this.renderedQuantity(freshHost);
      if (rendered !== null) st.confirmedQuantity = rendered;
      if (st.targetQuantity !== null && st.targetQuantity === st.confirmedQuantity) {
        st.targetQuantity = null;
        st.removeRequested = false;
      }
    }

    for (const [key, st] of this.states) {
      if (st.inFlight) continue;
      if (st.targetQuantity === null || st.targetQuantity === st.confirmedQuantity) {
        this.setBusy(key, false);
        continue;
      }
      const host = this.findHost(key);
      if (!host) { this.states.delete(key); continue; }
      const link = host.querySelector('a[href*="/cart/change"]');
      if (!link) { this.states.delete(key); continue; }

      const url = new URL(link.href, location.origin);
      url.searchParams.set('quantity', String(st.targetQuantity));
      link.setAttribute('href', url.pathname + url.search);

      st.inFlight = true;
      this.setBusy(key, true);
      this.allowSynthetic = true;
      link.click();
      return;
    }

    const active = this.chainActive();
    this.lockCheckout(active);
    if (!active) {
      for (const key of this.states.keys()) this.setBusy(key, false);
      this.restoreFocus();
    }
    // SK18: son söz uzlaştırmanındır — bekleyen iş yokken sepet doluysa buton
    // hiçbir koşulda pasif kalmaz.
    this.reconcileCheckout();
  },

  onError(event) {
    const line = event.target.closest && event.target.closest('line-item');
    const input = line && line.querySelector('[data-line-key]');
    const key = input ? input.dataset.lineKey : null;
    if (key && this.states.has(key)) {
      this.setBusy(key, false);
      this.states.delete(key);
    } else {
      this.states.clear();
    }
    this.lockCheckout(false);
    // SK18: hata yolunda da gerçek sepetle uzlaş — sepet doluysa buton aktif.
    this.reconcileCheckout();
  },

  init() {
    document.addEventListener('click', (e) => this.onClick(e), true);
    document.addEventListener('line-item:change', (e) => this.onLineChange(e));
    document.addEventListener('cart:error', (e) => this.onError(e));
    document.addEventListener('cart-drawer:refreshed', () => this.onRefreshed());

    // SK18 — KURTARMA KANCALARI
    // Çekmece açıldığında ve Safari sayfayı bfcache'ten geri getirdiğinde
    // istemci belleği ile gerçek sepet arasında fark kalmış olabilir; her iki
    // durumda da buton durumu güncel sepetle yeniden uzlaştırılır.
    document.addEventListener('dialog:after-show', (e) => {
      if (e.target && e.target.tagName === 'CART-DRAWER') this.reconcileCheckout();
    });
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) this.reconcileCheckout();
    });
  }
};

BeloraCartIntent.init();

/* --------------------------------------------------------------------------
 * SK7-L.1 — BEDEN SEÇİMİ CANLI DUYURUSU
 * --------------------------------------------------------------------------
 * Seçim özeti Liquid tarafından `aria-live="polite"` ile basılıyor, ancak
 * Prestige varyant değişiminde `<product-rerender>` ile satın alma alanının
 * TAMAMINI değiştiriyor. Değiştirilen (yeni oluşturulan) bir canlı bölge
 * tarayıcılar tarafından güvenilir biçimde DUYURULMAZ — bu yüzden duyuru
 * burada bir kez, Prestige'in KENDİ global canlı bölgesine yazılır
 * (`#status-announcement`, layout/theme.liquid:198). İkinci bir canlı bölge
 * AÇILMADI.
 *
 * ⚠️ Gereksiz tekrar yok: aynı metin üst üste duyurulmaz (`lastMessage`).
 * ⚠️ Sayfa ilk açılışında duyuru YAPILMAZ; yalnız gerçek bir kullanıcı
 *    seçiminden (`variant:change`) sonra duyurulur.
 * ⚠️ Mutation gözlemcisi kullanılmaz: `variant:change` Prestige'in kendi
 *    sözleşmesidir ve DOM değişimi tamamlandıktan sonra tetiklenir.
 *
 * JS düşerse: özet metni yine görünür kalır (Liquid basıyor), yalnız ekran
 * okuyucu duyurusu tek katmana (yerel `aria-live`) iner — bozulma DEĞİL.
 * -------------------------------------------------------------------------- */
const BeloraSizeSelection = {
  lastMessage: '',

  announce(message) {
    if (!message || message === this.lastMessage) return;

    const holder = document.getElementById('status-announcement');
    if (!holder) return;

    this.lastMessage = message;
    holder.textContent = '';
    // Prestige'in kendi duyuru deseniyle aynı: temizle, sonra yaz.
    setTimeout(() => {
      holder.textContent = message;
    }, 100);
  },

  onVariantChange() {
    // Yeniden render sonrası özet düğümü YENİDİR; her seferinde tazeden okunur.
    setTimeout(() => {
      const summary = document.querySelector('[data-belora-size-summary]');
      if (!summary || summary.dataset.sizeSelected !== 'true') return;

      this.announce((summary.textContent || '').trim());
    }, 0);
  },

  init() {
    document.addEventListener('variant:change', () => this.onVariantChange());
  }
};

BeloraSizeSelection.init();

/* --------------------------------------------------------------------------
 * SK7-K.3G.1 — KAYDIRMA ÇUBUĞU OLUĞU GENİŞLİĞİ
 * --------------------------------------------------------------------------
 * ÖLÇÜLEN KUSUR (1440, sepet çekmecesi açık):
 *   html.lock genişliği 1424.8 · ::part(base) 1424.8 · panel sağ kenarı 1424.8
 *   → panelin sağında 15.2px BOŞ ŞERİT. Canlı SKIMS'te panel sağ kenarı 1440.
 * Kök neden `html { scrollbar-gutter: stable }` (belora-skims.css · P1-A):
 * çekmece açıkken kaydırma çubuğu kaybolur ama oluğu REZERVE kalır.
 *
 * ⚠️ `100vw` çözüm DEĞİL: oluk rezerve iken viewport yüzde birimleri de oluğu
 * dışlıyor (ölçüldü: 100vw = 1425). Bu yüzden oluk genişliği burada ÖLÇÜLÜR.
 *
 * ⚠️ `innerWidth - clientWidth` İLE ÖLÇÜLMEZ. O fark yalnız kilit YOKKEN
 * doğrudur; çekmece açıkken 0 okunur ve son bilinen değer bayatlar. Ölçüldü:
 * 1440'ta 15 yazıldıktan sonra 390'a küçültülünce (çekmece hâlâ açık) değer 15
 * kalıyor, panel x15'e kayıyor ve sağdan 15.4px TAŞIYORDU — mobil regresyon.
 *
 * Bunun yerine kaydırma çubuğu KALINLIĞI kilitten bağımsız bir ölçü kutusuyla
 * okunur. `scrollbar-gutter: stable` oluğu içerik uzunluğundan bağımsız olarak
 * HER ZAMAN rezerve ettiği için, rezerve genişlik tam olarak bu kalınlıktır.
 * Overlay kaydırma çubuklu platformlarda (mobil, iOS, çoğu macOS) sonuç 0'dır
 * ve kural etkisiz kalır.
 *
 * Değer bir CSS değişkenine yazılır; geometriyi CSS kurar (belora-cart.css).
 * JS düşerse değişken 0 kalır ve davranış SK7-K.3G'deki hâline döner —
 * bozulma DEĞİL, yalnız şerit geri gelir.
 * -------------------------------------------------------------------------- */
const BeloraScrollbarWidth = {
  measure() {
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:absolute;top:-9999px;inline-size:100px;block-size:100px;overflow:scroll;visibility:hidden;pointer-events:none';
    document.body.appendChild(probe);
    const width = probe.offsetWidth - probe.clientWidth;
    probe.remove();

    // Makul olmayan değerler yazılmaz (zoom geçişi, tarayıcı farkı).
    if (width < 0 || width > 40) return;

    document.documentElement.style.setProperty('--belora-scrollbar-width', `${width}px`);
  },

  init() {
    this.measure();
    // Kilitliyken de güvenle yeniden ölçülebilir: ölçüm sayfanın kaydırma
    // durumuna DEĞİL, kutunun kendi kaydırma çubuğuna bakar.
    window.addEventListener('resize', () => this.measure());
    window.addEventListener('pageshow', () => this.measure());
  }
};

BeloraScrollbarWidth.init();

/* --------------------------------------------------------------------------
 * SK7-K.3G — HEDİYE ÇANTASI EKLENTİSİ
 * --------------------------------------------------------------------------
 * ÖLÇÜLEN CANLI SKIMS: teklif satırındaki GERÇEK checkbox işaretlenince
 * seçilen GERÇEK ürün varyantı sepete NORMAL bir satır olarak eklenir
 * (item_count 3 → 4, toplam $234 → $239) ve teklif satırı kaybolur.
 *
 * ⚠️ MUTATION SAHİPLİĞİ: bu bileşen YALNIZ `/cart/add.js` çağırır ve ardından
 * Prestige'in KENDİ `cart:refresh` olayını yayar — çekmeceyi, sepet sayacını
 * ve rozeti tema yeniden kurar. Adet koordinatörü ETKİLENMEZ: eklenen satır
 * ayrı bir `line` anahtarıdır, koordinatörün per-line durumu bozulmaz ve
 * bekleyen bir zincir varsa `cart-drawer:refreshed` ile normal seyrine devam
 * eder (koordinatörün tasarlanmış davranışı).
 *
 * ⚠️ UYDURMA YOK: fiyat Liquid tarafında gerçek varyanttan basılır, burada
 * hiçbir tutar hesaplanmaz. Hata durumunda Shopify'ın KENDİ mesajı gösterilir;
 * sahte başarı mesajı üretilmez ve checkbox gerçek duruma geri alınır.
 * -------------------------------------------------------------------------- */
class BeloraGiftBag extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('.belora-gift-bag__checkbox');
    this.errorNode = this.querySelector('.belora-gift-bag__error');
    if (!this.checkbox) return;
    this.checkbox.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    if (this.checkbox) this.checkbox.removeEventListener('change', this.#onChange);
  }

  #onChange = async () => {
    // Teklif satırı YALNIZ ürün sepette değilken basılır; işaretin kaldırılması
    // yalnız başarısız bir denemeden sonra oluşabilir ve yapacak iş yoktur.
    if (!this.checkbox.checked) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.checkbox.disabled = true;
    this.#setError('');

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });

      if (!response.ok) {
        let message = '';
        try {
          const payload = await response.json();
          message = payload.description || payload.message || '';
        } catch (error) {
          message = '';
        }
        this.#fail(message);
        return;
      }

      // Başarı: temanın kendi yeniden render zinciri devralır.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      this.#fail('');
    }
  };

  #fail(message) {
    this.checkbox.checked = false;
    this.checkbox.disabled = false;
    this.#setError(message);
  }

  #setError(message) {
    if (!this.errorNode) return;
    this.errorNode.textContent = message;
    this.errorNode.hidden = message === '';
  }
}

define('belora-gift-bag', BeloraGiftBag);

/* --------------------------------------------------------------------------
 * SK7-K.3G — İNDİRİM `Uygula` KÖPRÜSÜ
 * --------------------------------------------------------------------------
 * Ölçülen SKIMS promo alanında input'un yanında bir `Apply` düğmesi var.
 * Prestige'in indirim sistemi ise input'un `change` olayına bağlıdır
 * (`CartDiscountField` → `POST /cart/update.js`). Bu köprü YENİ BİR İNDİRİM
 * SİSTEMİ KURMAZ: yalnız o gerçek yolu tetikler.
 *
 * ⚠️ BOŞ KODDA HİÇBİR ŞEY YAPILMAZ. Boş değerle `change` yaymak, uygulanmış
 * indirim kodlarını SİLEN bir `discount=""` isteği üretirdi.
 *
 * Enter tuşu ayrı bir kancaya ihtiyaç duymaz: alan artık checkout formunun
 * DIŞINDA olduğu için Enter bir form göndermez, tarayıcı değeri işler ve
 * `change` doğal olarak tetiklenir.
 * -------------------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  const button = event.target.closest && event.target.closest('[data-belora-discount-apply]');
  if (!button || !button.closest('cart-drawer')) return;

  const field = button.closest('cart-discount-field');
  const input = field && field.querySelector('input:not([type="hidden"])');
  if (!input || input.value.trim() === '') return;

  input.dispatchEvent(new Event('change', { bubbles: true }));
});
/* --------------------------------------------------------------------------
 * SK19 — iOS SAFARI TAM SAYFA RESTORE TAZELIGI (sunucu-taban surumu)
 * --------------------------------------------------------------------------
 * KANIT (kullanici, gercek iPhone): Safari kapatilip bir sure sonra acildiginda
 * HICBIR etkilesim olmadan ESKI urun sayfasinin TAMAMI geri geldi - eski fiyat
 * (2.099), eski gorseller, eski duyuru metinleri, eski section sirasi ve eski
 * sticky CTA ayni ekranda. Tek bir onbellek bunlarin hepsini birden eskitemez;
 * iOS Safari CANLI SAYFA SNAPSHOT'ini geri yuklemistir.
 *
 * !! ILK PROTOTIP GERI ALINDI: canli (JS ile degistirilmis) DOM ile taze sunucu
 * HTML'i karsilastiriliyordu. Kullanici beden sectiginde varyant picker fiyat
 * blogunu ve section icerigini yeniden bastigi icin GUNCEL bir sayfa da "eski"
 * gorunebiliyordu -> yanlis pozitif ve yeniden yukleme dongusu riski.
 *
 * YENI TASARIM - IKI TARAF DA SUNUCUDAN:
 *   1. Sayfa ilk acildiginda ayni URL `no-store` ile bir kez cekilir ve o
 *      SUNUCU yanitindan bir TABAN parmak izi uretilir.
 *   2. Resume'da ayni URL yine `no-store` ile cekilir -> TAZE parmak izi.
 *   3. Karsilastirma yalniz SUNUCU<->SUNUCU yapilir. Canli DOM hic girmez,
 *      dolayisiyla kullanici etkilesimi yanlis pozitif uretemez.
 *
 * PARMAK IZI yalniz sayfa omru boyunca DEGISMEYEN alanlardan uretilir:
 *   asset surumleri (theme.js, belora.js) - duyuru seridindeki BUTUN mesajlar -
 *   urun medya ID listesi - ana urun DISINDAKI section ID sirasi - urun
 *   handle/ID - ana urun section kimligi.
 *   Fiyat parmak izine GIRMEZ; ayrica `/products/<handle>.js` uzerinden
 *   karsilastirilir (hardcode yok - Shopify'daki guncel deger gecerlidir).
 *
 * DONGU KORUMASI sure tabanli DEGIL, parmak izi tabanlidir: yeniden yuklemeden
 * once hedef taze parmak izi saklanir; yeni yuklemede sunucu ayni parmak izini
 * veriyorsa bir daha yuklenmez. Gelecekte gercek bir tema degisikligi oldugunda
 * parmak izi degisecegi icin duzeltme yeniden calisabilir.
 * -------------------------------------------------------------------------- */
const BeloraResumeFreshness = {
  TICK_MS: 15000,
  GAP_MS: 90000,
  NONCE: '_bfresh',
  KEY_BASE: 'belora:fresh-baseline',
  KEY_TARGET: 'belora:fresh-target',

  lastTick: Date.now(),
  baseline: null,
  inFlight: null,

  ss(fn, d) { try { return fn(window.sessionStorage); } catch (e) { return d; } },

  start() {
    if (window.location.pathname.indexOf('/products/') === -1) return;
    this.cleanNonce();
    setInterval(() => { this.lastTick = Date.now(); }, this.TICK_MS);

    window.addEventListener('pageshow', (e) => { if (e.persisted) this.onResume(true); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.onResume(false);
    });
    window.addEventListener('focus', () => this.onResume(false));

    // Taban parmak izi: sayfa ilk acildiginda SUNUCUDAN alinir.
    this.captureBaseline();
  },

  cleanNonce() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(this.NONCE)) return;
    url.searchParams.delete(this.NONCE);
    window.history.replaceState(window.history.state, '', url.toString());
  },

  key(name) { return name + ':' + window.location.pathname; },

  async captureBaseline() {
    const stored = this.ss(function (s) { return s.getItem(this.key(this.KEY_BASE)); }.bind(this), null);
    if (stored) { this.baseline = stored; return; }
    const fp = await this.serverFingerprint();
    if (!fp) return;
    this.baseline = fp;
    this.ss(function (s) { s.setItem(this.key(this.KEY_BASE), fp); }.bind(this));
  },

  onResume(persisted) {
    const gap = Date.now() - this.lastTick;
    if (!persisted && gap <= this.GAP_MS) return;
    this.lastTick = Date.now();
    if (this.inFlight) return;                 // tek in-flight kontrol
    const self = this;
    this.inFlight = this.check().finally(function () { self.inFlight = null; });
  },

  /** Sunucudan taze HTML + product JSON alip SABIT alanlardan parmak izi uretir. */
  async serverFingerprint() {
    const ctrl = new AbortController();
    const timer = setTimeout(function () { ctrl.abort(); }, 8000);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set(this.NONCE, String(Date.now()));
      const res = await fetch(url.toString(), {
        cache: 'no-store', credentials: 'same-origin', signal: ctrl.signal
      });
      if (!res.ok) return null;
      if (new URL(res.url).origin !== window.location.origin) return null;
      const ct = res.headers.get('content-type') || '';
      if (ct.indexOf('text/html') === -1) return null;
      const html = await res.text();
      if (html.length < 5000) return null;      // bot korumasi / ana sayfa yaniti

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const main = doc.querySelector('[id^="shopify-section"][id*="__main"]');
      if (!main) return null;                   // beklenen urun section'i yok
      const vp = doc.querySelector('variant-picker');
      const handle = vp ? vp.getAttribute('handle') : null;
      if (handle && window.location.pathname.indexOf(handle) === -1) return null;

      const assets = Array.prototype.slice
        .call(doc.querySelectorAll('script[src*="belora.js"], script[src*="theme.js"]'))
        .map(function (s) { return (s.getAttribute('src') || '').split('/').pop(); })
        .sort().join('|');
      const announcement = Array.prototype.slice
        .call(doc.querySelectorAll('[class*="announcement"], [class*="promo"]'))
        .map(function (el) { return el.textContent.replace(/\s+/g, ' ').trim(); })
        .filter(Boolean).sort().join('~');
      const media = Array.prototype.slice
        .call(doc.querySelectorAll('[data-media-id]'))
        .map(function (el) { return el.getAttribute('data-media-id'); }).join(',');
      const sections = Array.prototype.slice
        .call(doc.querySelectorAll('[id^="shopify-section"]'))
        .map(function (el) { return el.id; })
        .filter(function (id) { return id.indexOf('__main') === -1; }).join(',');

      let productPart = '';
      if (handle) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const pRes = await fetch(root + 'products/' + handle + '.js',
          { cache: 'no-store', credentials: 'same-origin', signal: ctrl.signal });
        if (pRes.ok) {
          const p = await pRes.json();
          // Fiyat hardcode EDILMEZ: Shopify'in o anki degeri kullanilir.
          productPart = [p.id, p.price, p.compare_at_price,
            (p.media || []).map(function (m) { return m.id; }).join('.'),
            (p.variants || []).map(function (v) { return v.id; }).join('.')].join(';');
        }
      }
      return [assets, announcement, media, sections, main.id, handle, productPart].join('##');
    } catch (e) {
      return null;                              // ag/timeout/parse: SAYFAYI BOZMA
    } finally { clearTimeout(timer); }
  },

  async check() {
    const fresh = await this.serverFingerprint();
    if (!fresh) return;
    if (!this.baseline) {
      this.baseline = fresh;
      this.ss(function (s) { s.setItem(this.key(this.KEY_BASE), fresh); }.bind(this));
      return;
    }
    if (fresh === this.baseline) return;         // fark yok -> hicbir sey yapma

    // Dongu korumasi: bu taze parmak izi icin zaten bir kez yuklendiysek dur.
    const target = this.ss(function (s) { return s.getItem(this.key(this.KEY_TARGET)); }.bind(this), null);
    if (target === fresh) return;
    this.ss(function (s) { s.setItem(this.key(this.KEY_TARGET), fresh); }.bind(this));
    // Yeni yuklemede taban SUNUCUDAN yeniden alinsin; aksi halde bu sekmede
    // taban surekli eski kalir ve modul yalnizca guard'a bagimli hale gelir.
    this.ss(function (s) { s.removeItem(this.key(this.KEY_BASE)); }.bind(this));

    const url = new URL(window.location.href);
    url.searchParams.set(this.NONCE, String(Date.now()));
    window.location.replace(url.toString());
  }
};

BeloraResumeFreshness.start();
